// ============================================================
//  EXPERT DETAIL PAGE — ADMIN DASHBOARD
//  Path: src/pages/ExpertDetailPage.tsx
//
//  Tabbed detail view for one expert:
//    Profile     — contact info, bio, skills, background check,
//                  company fields, subscription & payout
//    Categories  — rate/experience/certification per category
//    Jobs        — every task they've worked, click into any one
//                  for full payment/location/timing detail
//    Earnings    — real Stripe balance + job-by-job ledger, same
//                  data the expert's own mobile Earnings screen shows
//    Documents   — onboarding progress + per-document review, now
//                  split into Identity / Background Check /
//                  Categories sub-tabs (see docSubTab below).
//                  Company documents (registration, liability,
//                  commercial auto) are DELIBERATELY never shown
//                  here at all — see excludeCompanyDocs — they are
//                  reviewed exclusively on CompaniesPage, which has
//                  its own Approve/Reject actions for the company
//                  as a whole.
//
//  REDESIGN NOTE: layout only — all data fetching, handlers, and
//  API shapes are unchanged from the previous version. Changes:
//    - header is now a real summary card (status, quick stats,
//      Approve / Override actions surfaced immediately)
//    - tabs show icons + live counts (pending docs, job count)
//    - profile info split into grouped section cards instead of
//      one long flat list
//    - categories & documents render as 2-column grids on wider
//      screens instead of a single stacked column
// ============================================================

import React, { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import { getExpertTimeline, getExpertById, listTasksForExpert, getExpertEarnings, getTaskDetail, listExpertDocuments, getDocumentViewUrl, getExpertOnboardingStatus, getExpertSetupTimeline, verifyDocument, updateExpertStatus, overrideExpertSubscription, removeExpertCategory, deleteExpertDocument, removeExpertVehicle, listExpertInsurancePolicies, verifyExpertInsurancePolicy, updateExpertInsurancePolicyExpiry, deleteExpertInsurancePolicy, dismissExpertIssue, restoreExpertIssue, listExpertIssueDismissals } from '../services/admin.service'
import { isReauthRequired } from '../config/api'
import { Expert, Task, ExpertEarnings, AdminExpertDocument, OnboardingStatus, OnboardingItemStatus, ExpertInsurancePolicy, InsuranceType, DismissedIssue } from '../types'
import { useAuth } from '../context/AuthContext'
import {
  ArrowLeft, CheckCircle2, Circle, AlertCircle, Clock, ShieldCheck, FileText,
  User, Briefcase, ListChecks, DollarSign, Building2, Copy, Check, Activity,
} from 'lucide-react'
import { TimelineSection } from '../components/ActivityTimeline'
import SupportMessageButton from '../components/SupportMessageButton'
// 2026-10-10: open help requests from this tasker, under their name.
import SupportRequestsBadge from '../components/SupportRequestsBadge'

type Tab = 'profile' | 'categories' | 'jobs' | 'earnings' | 'documents' | 'insurance' | 'activity'

// Rounds to cents the same way the backend's own round2 does — used
// only to derive commissionAmount client-side (it isn't a stored
// column; see the note where it's computed below).
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

// What Stripe setup looks like to an admin. Taskers are paid straight into
// their OWN Stripe account (direct charges), so this is whether that
// account can take payments. Mirrors Expert.stripeBankStatus.
const STRIPE_STATUS_INFO: Record<string, { label: string; className: string }> = {
  not_started:          { label: 'Not started — no Stripe account yet',                 className: 'text-gray-400' },
  incomplete:           { label: 'Incomplete — the tasker still has to give Stripe information', className: 'text-yellow-400' },
  pending_verification: { label: 'Stripe is verifying their details',                    className: 'text-yellow-400' },
  restricted:           { label: 'Restricted by Stripe — cannot be paid until fixed',    className: 'text-red-400' },
  disconnected:         { label: 'Disconnected — they removed the platform from their Stripe account', className: 'text-red-400' },
  verified:             { label: 'Verified — can be paid',                               className: 'text-green-400' },
}

const TASK_STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-600/20 text-blue-400', assigned: 'bg-yellow-600/20 text-yellow-400',
  pending_completion: 'bg-orange-600/20 text-orange-400', completed: 'bg-green-600/20 text-green-400',
  disputed: 'bg-red-600/20 text-red-400', cancelled: 'bg-gray-600/20 text-gray-400',
}

const EXPERT_STATUS_COLORS: Record<string, string> = {
  approved: 'bg-green-600/20 text-green-400',
  pending: 'bg-yellow-600/20 text-yellow-400',
  under_review: 'bg-blue-600/20 text-blue-400',
  background_check: 'bg-purple-600/20 text-purple-400',
  rejected: 'bg-red-600/20 text-red-400',
  suspended: 'bg-gray-600/20 text-gray-400',
}

const TAB_ICONS: Record<Tab, React.ElementType> = {
  profile: User, categories: Briefcase, jobs: ListChecks, earnings: DollarSign, documents: FileText, insurance: ShieldCheck, activity: Activity,
}

const TAB_LABELS: Record<Tab, string> = {
  profile: 'Profile', categories: 'Categories', jobs: 'Assigned Jobs', earnings: 'Earnings', documents: 'Documents', insurance: 'Insurance', activity: 'Activity Timeline',
}

const INSURANCE_TYPE_LABELS: Record<string, string> = {
  liability:              'Liability',
  cargo:                  'Cargo',
  commercial_auto:        'Commercial Auto',
  professional_indemnity: 'Professional Indemnity',
}

// ── Document review state ────────────────────────────────────
// FIX: every badge on this page read `doc.isVerified ? 'Verified' :
// 'Pending Review'`. That is two states for a field that has four, and
// the missing one matters most: a document THIS DASHBOARD rejected —
// isVerified false, rejectionReason set — displayed as "Pending Review"
// indefinitely, so the next admin (or the same one, later) saw a queue
// item that looked untouched and had no idea it had already been
// refused and was waiting on the expert.
//
// This is the identical defect the expert app fixed in DocumentsScreen
// ("a REJECTED government ID read as 'Pending Review' indefinitely"),
// and it survived here because this page derives its own state from raw
// document rows instead of reading the resolver.
//
// Derivation matches deriveDocumentItem in
// expert/service/onboardingStatus.service.js exactly. Where the
// resolver's answer is available (see onboarding below) that is
// preferred; this is the fallback for documents the resolver does not
// surface as items, so the two can never disagree about what the four
// states mean.
const docStatusOf = (doc: AdminExpertDocument): OnboardingItemStatus => {
  if (doc.isDraft)         return 'draft'
  if (doc.isVerified)      return 'verified'
  if (doc.rejectionReason) return 'action_required'
  return 'submitted'
}

// FIX: every count badge on this page (the top summary, every category
// header, every vehicle header) tallied `!doc.isVerified` as one number —
// "pending". That's the same conflation docStatusOf above exists to fix,
// just relocated to the counters instead of the individual badges: a
// document THIS DASHBOARD rejected counts here identically to one nobody
// has looked at yet, so the FIRST thing an admin sees on this page never
// actually shows that a rejection happened. Splits the same `!isVerified`
// set into the two states docStatusOf already knows how to tell apart.
const tallyReviewStatus = (docs: AdminExpertDocument[]) => {
  let pending = 0
  let rejected = 0
  for (const d of docs) {
    const status = docStatusOf(d)
    if (status === 'action_required') rejected++
    else if (status !== 'verified') pending++
  }
  return { pending, rejected, total: pending + rejected }
}

// Short label for a count pill — "2 rejected", "1 pending", "1 rejected,
// 2 pending", or "all reviewed" when both are zero.
const reviewSummaryLabel = ({ pending, rejected }: { pending: number; rejected: number }) => {
  if (pending === 0 && rejected === 0) return 'all reviewed'
  const parts: string[] = []
  if (rejected > 0) parts.push(`${rejected} rejected`)
  if (pending  > 0) parts.push(`${pending} pending`)
  return parts.join(', ')
}

const DOC_STATUS_LABEL: Record<OnboardingItemStatus, string> = {
  not_started:     'Not uploaded',
  draft:           'Not submitted',
  submitted:       'Pending review',
  action_required: 'Rejected — awaiting re-upload',
  verified:        'Verified',
  failed:          'Failed',
}

const DOC_STATUS_CLASS: Record<OnboardingItemStatus, string> = {
  not_started:     'bg-gray-600/20 text-gray-400',
  draft:           'bg-gray-600/20 text-gray-400',
  submitted:       'bg-yellow-600/20 text-yellow-400',
  action_required: 'bg-red-600/20 text-red-400',
  verified:        'bg-green-600/20 text-green-400',
  failed:          'bg-red-600/20 text-red-400',
}

const STEP_STATUS_CLASS: Record<string, string> = {
  complete:        'bg-green-600/20 text-green-400',
  submitted:       'bg-yellow-600/20 text-yellow-400',
  action_required: 'bg-red-600/20 text-red-400',
  in_progress:     'bg-blue-600/20 text-blue-400',
  not_started:     'bg-gray-600/20 text-gray-400',
  locked:          'bg-gray-700/20 text-gray-500',
}

const IDENTITY_DOC_TYPES = new Set(['selfie', 'government_id_front', 'government_id_back'])
// Split out from IDENTITY_DOC_TYPES on purpose — the background
// check is its own review concern with its own status machine
// (Certn vs. uploaded proof, pass/fail/manual_review), not just
// another identity document. Kept as its own set so it can be its
// own sub-tab rather than lumped in with government ID and selfie.
const BACKGROUND_CHECK_DOC_TYPES = new Set(['external_background_check'])
// Union of the two, used only where the code needs to ask "is this
// a personal document at all" (e.g. excluding both from the
// category-grouped bucket below) without caring which of the two.
// A function rather than `new Set([...a, ...b])` on purpose — that
// spread requires --downlevelIteration or an ES2015+ target, which
// this project's tsconfig doesn't set, and CRA's build fails hard
// on it (TS2802) rather than silently downleveling.
const isPersonalDocType = (type: string) => IDENTITY_DOC_TYPES.has(type) || BACKGROUND_CHECK_DOC_TYPES.has(type)
// FIX: this used to be a single `d.type !== 'business_registration'`
// check inlined at every fetch site below. That only ever excluded
// ONE of the three company document types — liability_insurance and
// commercial_auto_insurance were still leaking into this individual
// expert's own Documents tab, mixed in with their personal ID and
// category documents. That's not just visual clutter: it also
// silently corrupted the "all documents verified / ready to
// approve" calculation further down (tallyReviewStatus(documents)),
// which could read as blocked — or wrongly read as clear — based on
// an unrelated company insurance document's review state. Company
// documents are reviewed EXCLUSIVELY on CompaniesPage; they must
// never reach this page's `documents` state at all.
//
// UPDATED: this exclusion is no longer unconditional for
// liability_insurance / commercial_auto_insurance. Category.
// requiresLiabilityInsurance (schema.prisma) now lets an INDIVIDUAL
// (non-company) expert also be required to upload a
// liability_insurance document — see CategoriesScreen.tsx's
// uploadLiabilityInsuranceDocument. There is no CompaniesPage entry
// for an individual expert at all, so unconditionally stripping this
// type here would make that document permanently unreachable by any
// admin review screen — uploaded successfully, visible nowhere,
// forever stuck "pending" on the expert's own onboarding checklist.
//
// business_registration remains ALWAYS excluded regardless of
// expertType — there is no individual-expert flow that produces it,
// so it stays exclusively a CompaniesPage concern. liability_insurance
// and commercial_auto_insurance are only excluded when the expert IS
// a company (where they are correctly reviewed on CompaniesPage
// instead) — for an individual expert they must surface here, since
// this is the only page that will ever show them.
const COMPANY_ONLY_DOC_TYPES = new Set(['business_registration'])
const COMPANY_INSURANCE_DOC_TYPES = new Set(['liability_insurance', 'commercial_auto_insurance'])
const excludeCompanyDocs = (docs: AdminExpertDocument[], isCompany: boolean) =>
  docs.filter((d) => {
    if (COMPANY_ONLY_DOC_TYPES.has(d.type)) return false
    if (isCompany && COMPANY_INSURANCE_DOC_TYPES.has(d.type)) return false
    return true
  })

// Document types that represent proof of a real insurance policy —
// approving one of these should also create/update the matching
// ExpertInsurancePolicy row (see admin.service.js's verifyDocument),
// which is the ONLY table the fee-waiver check at hire time reads.
// Mirrors INSURANCE_LINKED_DOC_TYPES on the backend exactly; kept in
// sync manually since this is a small, stable set.
// A generic 'insurance_proof' document (for example a vehicle's personal
// insurance) is NOT insurance here: it only counts when its requirement has
// "Proves Insurance" set (isInsuranceLinkedDoc below). Approving it otherwise
// is a plain approval that never creates a policy or a waiver.
const INSURANCE_LINKED_DOC_TYPES = new Set(['liability_insurance', 'commercial_auto_insurance'])
// The two specific types map to exactly one InsuranceType each — no
// need to ask the admin. Only the generic 'insurance_proof' type
// requires them to say which coverage this particular certificate
// proves.
const DOC_TYPE_TO_INSURANCE_TYPE: Partial<Record<string, InsuranceType>> = {
  liability_insurance:       'liability',
  commercial_auto_insurance: 'commercial_auto',
}
const INSURANCE_TYPE_OPTIONS: { value: InsuranceType; label: string }[] = [
  { value: 'liability',              label: 'Liability' },
  { value: 'cargo',                  label: 'Cargo' },
  { value: 'commercial_auto',        label: 'Commercial Auto' },
  { value: 'professional_indemnity', label: 'Professional Indemnity' },
]

// FIX: every "Approve" button on this page decided whether to open
// the insurance-verify modal (Provider + Expiry Date, required by
// the backend before an ExpertInsurancePolicy row can be created)
// by checking INSURANCE_LINKED_DOC_TYPES.has(doc.type) — the
// document's OWN type. That's correct for the two fixed company-
// level types (liability_insurance, commercial_auto_insurance), but
// a category-checklist document always uploads as type: 'license'
// regardless of what it actually proves (see CategoriesScreen.tsx's
// uploadLicenseDocument) — so a "Liability Insurance" requirement's
// document has doc.type === 'license', which this check has never
// recognized as insurance-linked. Clicking Approve on one called
// handleVerifyDocument(doc.id, true) directly, with no provider —
// and the backend correctly rejects that with "provider is required
// to verify an insurance document", leaving admin stuck: Approve
// fails every time, with no path to the form that would actually
// let them supply what's missing.
//
// This mirrors the exact fix already made on the backend
// (admin.service.js's verifyDocument): the authoritative signal is
// the LINKED REQUIREMENT's insuranceType (set by admin at category-
// configuration time — see CategoriesPage.tsx's "Proves Insurance"
// dropdown), not the document's own type string. Checking that here
// too is what makes Approve open the right form for a category-
// checklist insurance document instead of failing silently against
// a validation the button never accounted for.
const isInsuranceLinkedDoc = (doc: AdminExpertDocument) =>
  INSURANCE_LINKED_DOC_TYPES.has(doc.type) || !!doc.licenseRequirement?.insuranceType

// The InsuranceType this document proves, when the system already
// knows it — the fixed 1:1 map for the two company-level types,
// otherwise the linked requirement's own insuranceType. Mirrors
// admin.service.js's resolution order (minus the admin-supplied
// fallback, which is what the modal's dropdown IS). undefined means
// nothing authoritative is known and admin must be asked.
//
// FIX: this rule was written out twice, differently — the modal's
// dropdown rendered on DOC_TYPE_TO_INSURANCE_TYPE alone while
// needsTypeChoice in submitInsuranceVerify also consulted the
// requirement. For a category-checklist 'license' document with a
// known requirement.insuranceType that meant admin was shown a
// coverage dropdown whose value was then silently discarded on
// submit. One function, used by both, is what keeps the question
// asked and the answer sent from disagreeing.
const resolveInsuranceType = (doc: AdminExpertDocument): InsuranceType | undefined =>
  DOC_TYPE_TO_INSURANCE_TYPE[doc.type] || doc.licenseRequirement?.insuranceType || undefined

// 2026-10-12: Approve used to open the date form ONLY for "Proves Insurance"
// documents, so a licence, a certificate or a personal / vehicle insurance
// document was approved in one click with nobody checking its expiry date.
// Now every document that carries an expiry date opens the confirm form
// (date pre-filled from the tasker). Personal insurance also asks for the
// insurance company. Identity documents are not affected (no expiry).
const asksInsurer = (doc: AdminExpertDocument) =>
  isInsuranceLinkedDoc(doc) || doc.type === 'insurance_proof'
// Every document in the category / vehicle cards asks, EXCEPT the few types
// that never expire. (Asking by default means a document type added later
// is checked too, instead of slipping through in one click.)
const NO_EXPIRY_DOC_TYPES = new Set(['selfie', 'external_background_check', 'government_id_front', 'government_id_back'])
const needsDateConfirm = (doc: AdminExpertDocument) =>
  isInsuranceLinkedDoc(doc) || !NO_EXPIRY_DOC_TYPES.has(doc.type)

const PERSONAL_DOC_LABELS: Record<string, string> = {
  selfie: 'Selfie',
  government_id_front: 'Government ID — Front',
  government_id_back: 'Government ID — Back',
  external_background_check: 'Background Check (Uploaded Proof)',
}

const ExpertDetailPage: React.FC = () => {
  // FIX (production-readiness pass): this page — approve/reject expert,
  // subscription override, document verify/delete, vehicle/category
  // removal, insurance policy verify — had NO role check at all, even
  // though its route admits customer_service (App.tsx's experts/:id
  // route). Every comparably risky page elsewhere in this codebase
  // (CategoriesPage, BackgroundChecksPage, VehicleVerificationsPage)
  // explicitly restricts mutations to super_admin/admin. This was the
  // one place a customer_service account could approve or reject an
  // expert, override their subscription, or delete their documents
  // with no gate whatsoever.
  const { user } = useAuth()
  const canModify = user?.roles.includes('super_admin') || user?.roles.includes('admin')

  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [expert, setExpert]       = useState<Expert | null>(null)
  const [tab, setTab]             = useState<Tab>('profile')
  const [tasks, setTasks]         = useState<Task[]>([])
  const [tasksLoaded, setTasksLoaded] = useState(false)
  const [earnings, setEarnings]   = useState<ExpertEarnings | null>(null)
  const [earningsLoaded, setEarningsLoaded] = useState(false)
  // A failed load used to leave "Loading earnings..." on screen forever.
  const [earningsError, setEarningsError] = useState('')
  const [rawDocuments, setRawDocuments] = useState<AdminExpertDocument[]>([])
  // Derived, not stored directly — recomputes automatically whenever
  // either rawDocuments OR expert (specifically expert.expertType)
  // changes. This is what avoids the race condition of fetching
  // documents before the expert's own profile (and therefore
  // expertType) has loaded: no matter which resolves first, this
  // memo is always correct once BOTH are available, without needing
  // every one of the several fetch call sites below to coordinate
  // ordering themselves.
  const documents = useMemo(
    () => excludeCompanyDocs(rawDocuments, expert?.expertType === 'company'),
    [rawDocuments, expert]
  )
  const [documentsLoaded, setDocumentsLoaded] = useState(false)
  // The resolver's answer — the same object the expert is looking at in
  // their own app right now. Everything about "what is outstanding"
  // comes from here rather than being recomputed from `documents`.
  const [onboarding, setOnboarding] = useState<OnboardingStatus | null>(null)
  const [actingDocId, setActingDocId] = useState<string | null>(null)
  const [previewDoc, setPreviewDoc] = useState<AdminExpertDocument | null>(null)
  // Architecture doc section 12 — the preview modal no longer reads
  // doc.fileUrl directly (that's still on the list response, but
  // opening a document is now a distinct, audited action). previewUrl
  // is the short-lived signed link returned by getDocumentViewUrl,
  // fetched fresh each time the modal opens; previewLoading/previewError
  // cover the wait and the two ways the fetch can fail — a real error,
  // or (previewMfaRequired) this admin's session having no
  // authenticator-app factor on it once ADMIN_MFA_ENFORCED is on.
  const [previewUrl, setPreviewUrl]           = useState<string | null>(null)
  const [previewLoading, setPreviewLoading]   = useState(false)
  const [previewError, setPreviewError]       = useState('')
  const [previewMfaRequired, setPreviewMfaRequired] = useState(false)
  const [reuploadModal, setReuploadModal] = useState<AdminExpertDocument | null>(null)
  const [reuploadReason, setReuploadReason] = useState('')

  // ── Insurance-linked document approval ──────────────────────
  // Approving a document whose type is in INSURANCE_LINKED_DOC_TYPES
  // needs provider + expiry (+ insuranceType for the generic
  // insurance_proof type) before it can be sent — see
  // admin.service.js's verifyDocument for why these are required
  // rather than optional. Opens instead of calling
  // handleVerifyDocument directly; every other document type keeps
  // the existing one-click Approve behaviour untouched.
  const [insuranceVerifyModal, setInsuranceVerifyModal] = useState<AdminExpertDocument | null>(null)
  const [insuranceVerifyProvider, setInsuranceVerifyProvider] = useState('')
  const [insuranceVerifyExpiry, setInsuranceVerifyExpiry] = useState('')
  const [insuranceVerifyNoExpiry, setInsuranceVerifyNoExpiry] = useState(false)
  const [insuranceVerifyType, setInsuranceVerifyType] = useState<InsuranceType>('liability')
  const [insuranceVerifyError, setInsuranceVerifyError] = useState('')

  // The tasker's own expiry date (entered when they uploaded), as the
  // yyyy-mm-dd value a date input needs. Local date parts, so the day
  // never shifts across time zones.
  const declaredExpiryInput = (doc: AdminExpertDocument | null): string => {
    const raw = (doc as any)?.expertDeclaredExpiresAt
    if (!raw) return ''
    const d = new Date(raw)
    if (isNaN(d.getTime())) return ''
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  // What the form starts with: the date the admin already confirmed (if this is
  // a second approval), otherwise the tasker's date.
  const prefillExpiryInput = (doc: AdminExpertDocument): string => {
    const d = doc as any
    if (d.expiryConfirmedAt) {
      if (!d.adminConfirmedExpiresAt) return ''
      const c = new Date(d.adminConfirmedExpiresAt)
      return isNaN(c.getTime()) ? '' : `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, '0')}-${String(c.getDate()).padStart(2, '0')}`
    }
    return declaredExpiryInput(doc)
  }

  const openInsuranceVerifyModal = (doc: AdminExpertDocument) => {
    setInsuranceVerifyModal(doc)
    setInsuranceVerifyProvider('')
    // 2026-10-09: pre-filled with the date the tasker entered at upload, so
    // the admin checks it against the certificate instead of typing it
    // again. The admin can still change it — what is saved is what the
    // admin confirms.
    setInsuranceVerifyExpiry(prefillExpiryInput(doc))
    setInsuranceVerifyNoExpiry(!!(doc as any).expiryConfirmedAt && !(doc as any).adminConfirmedExpiresAt)
    // FIX: previously only checked DOC_TYPE_TO_INSURANCE_TYPE[doc.type],
    // which is empty for a category-checklist 'license' document
    // regardless of what it actually proves — silently defaulting to
    // 'liability' even for a Commercial Auto or Professional
    // Indemnity requirement. doc.licenseRequirement?.insuranceType is
    // the authoritative source (set by admin at category-config
    // time — see CategoriesPage.tsx's "Proves Insurance" dropdown)
    // and takes priority; the fixed-type map remains the fallback for
    // the two company-level document types, which never carry a
    // licenseRequirement at all.
    setInsuranceVerifyType(resolveInsuranceType(doc) || 'liability')
    setInsuranceVerifyError('')
  }

  const submitInsuranceVerify = async () => {
    if (!insuranceVerifyModal) return
    const provider = insuranceVerifyProvider.trim()
    const linked = isInsuranceLinkedDoc(insuranceVerifyModal)
    if (asksInsurer(insuranceVerifyModal) && !provider) {
      setInsuranceVerifyError(linked ? 'Provider is required.' : 'Insurance company is required.')
      return
    }
    if (!insuranceVerifyNoExpiry && !insuranceVerifyExpiry) {
      setInsuranceVerifyError('Enter an expiry date, or check "No expiry".')
      return
    }
    setInsuranceVerifyError('')
    // FIX: previously only true when the document's own type wasn't
    // in the fixed map — which meant a category-checklist 'license'
    // document with a KNOWN requirement.insuranceType still showed
    // the "which coverage does this prove" dropdown, asking admin to
    // manually re-pick something the system already knows from how
    // the category was configured. Worse, whatever admin picked in
    // that dropdown was sent as-is and could silently disagree with
    // the requirement's actual insuranceType — the exact
    // "mismatched type" risk discussed when this dropdown was first
    // built. Only show it now when there's genuinely no authoritative
    // answer already known.
    const needsTypeChoice = linked && !resolveInsuranceType(insuranceVerifyModal)
    const ok = await handleVerifyDocument(insuranceVerifyModal.id, true, undefined, {
      insuranceType: needsTypeChoice ? insuranceVerifyType : undefined,
      provider,
      expiresAt: insuranceVerifyNoExpiry ? null : new Date(insuranceVerifyExpiry).toISOString(),
    })
    if (ok) setInsuranceVerifyModal(null)
  }

  const [expandedDocSections, setExpandedDocSections] = useState<Set<string>>(new Set())
  // Internal navigation within the Documents tab — see the header
  // note above about why company docs, identity docs, background
  // check, and category docs are now genuinely separate sections
  // rather than one long scrolling page.
  const [docSubTab, setDocSubTab] = useState<'identity' | 'background' | 'categories'>('identity')
  const [approvingExpert, setApprovingExpert] = useState(false)
  const [showRejectExpert, setShowRejectExpert] = useState(false)
  const [rejectExpertReason, setRejectExpertReason] = useState('')
  const [rejectingExpert, setRejectingExpert] = useState(false)
  const [showSubOverride, setShowSubOverride] = useState(false)
  const [subOverrideStatus, setSubOverrideStatus] = useState<'free' | 'active' | 'past_due'>('active')
  const [subOverrideRenewsAt, setSubOverrideRenewsAt] = useState('')
  const [subOverrideReason, setSubOverrideReason] = useState('')
  const [savingSubOverride, setSavingSubOverride] = useState(false)
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [insurancePolicies, setInsurancePolicies]   = useState<ExpertInsurancePolicy[]>([])
  const [insurancePoliciesLoaded, setInsurancePoliciesLoaded] = useState(false)
  const [actingPolicyId, setActingPolicyId]         = useState<string | null>(null)
  const [policyNoteModal, setPolicyNoteModal]       = useState<ExpertInsurancePolicy | null>(null)
  const [policyNoteText, setPolicyNoteText]         = useState('')
  const [policyExpiryModal, setPolicyExpiryModal]   = useState<ExpertInsurancePolicy | null>(null)
  const [policyExpiryText, setPolicyExpiryText]     = useState('')
  const [policyDeleteModal, setPolicyDeleteModal]   = useState<ExpertInsurancePolicy | null>(null)
  const [loading, setLoading]     = useState(true)
  const [loadError, setLoadError] = useState('')
  const [idCopied, setIdCopied]   = useState(false)
  const [dismissingIssue, setDismissingIssue] = useState<string | null>(null)
  const [issueActionError, setIssueActionError] = useState('')
  const [showDismissed, setShowDismissed] = useState(false)
  const [dismissedIssues, setDismissedIssues] = useState<DismissedIssue[] | null>(null)
  const [dismissedLoading, setDismissedLoading] = useState(false)
  const [restoringIssue, setRestoringIssue] = useState<string | null>(null)

  // Pulled out of the effect below (not just inline) so the Issues
  // panel's "Clear" button can re-run it after a dismiss, without
  // duplicating the fetch logic.
  const fetchExpert = async () => {
    if (!id) return
    try {
      // FIX: this used to call listExperts({ limit: 1000 }) and find
      // the match client-side. The backend clamps limit to 100, so the
      // response never contained more than the first 100 experts and
      // anyone below that line opened to a blank "Expert not found" —
      // a bug that grew quietly with every signup. There is a by-id
      // endpoint now: GET /api/admin/experts/:id.
      const res = await getExpertById(id)
      setExpert(res.data.data)
    } catch (err: any) {
      // A real 404 and a failed request are different things, and the
      // page rendered both as "not found".
      if (err?.response?.status === 404) {
        setExpert(null)
      } else {
        console.error('Failed to fetch expert:', (err as any)?.response?.status, (err as any)?.message)
        setLoadError('Could not load this expert — check your connection and try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  // fetchExpert is redefined every render (it closes over state setters,
  // not props), so listing it as a dependency would re-run this effect on
  // every render instead of only when the route's :id changes, which is
  // the intended behavior.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchExpert() }, [id])

  // Fixing the underlying problem clears an issue automatically — this
  // is only for "I've looked at this, stop showing it to me". It stays
  // cleared unless a NEW occurrence of the same issue happens (see
  // admin.service.js's IssueDismissal comment).
  const handleDismissIssue = async (issueCode: string) => {
    if (!id) return
    setDismissingIssue(issueCode)
    setIssueActionError('')
    try {
      await dismissExpertIssue(id, issueCode)
      await fetchExpert()
    } catch (err: any) {
      console.error('Failed to dismiss issue:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not clear this issue — try again.')
    } finally {
      setDismissingIssue(null)
    }
  }

  const loadDismissedIssues = async () => {
    if (!id) return
    setDismissedLoading(true)
    try {
      const res = await listExpertIssueDismissals(id)
      setDismissedIssues(res.data.data)
    } catch (err: any) {
      console.error('Failed to fetch dismissed issues:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not load dismissed issues — try again.')
    } finally {
      setDismissedLoading(false)
    }
  }

  const toggleShowDismissed = () => {
    const next = !showDismissed
    setShowDismissed(next)
    if (next && dismissedIssues === null) loadDismissedIssues()
  }

  const handleRestoreIssue = async (issueCode: string) => {
    if (!id) return
    setRestoringIssue(issueCode)
    setIssueActionError('')
    try {
      await restoreExpertIssue(id, issueCode)
      await Promise.all([fetchExpert(), loadDismissedIssues()])
    } catch (err: any) {
      console.error('Failed to restore issue:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not restore this issue — try again.')
    } finally {
      setRestoringIssue(null)
    }
  }

  useEffect(() => {
    if (!id) return
    if (tab === 'jobs' && !tasksLoaded) {
      listTasksForExpert(id).then((res) => { setTasks(res.data.data); setTasksLoaded(true) })
        .catch((err) => console.error('Failed to fetch expert jobs:', (err as any)?.response?.status, (err as any)?.message))
    }
    if (tab === 'earnings' && !earningsLoaded) {
      setEarningsError('')
      getExpertEarnings(id).then((res) => { setEarnings(res.data.data); setEarningsLoaded(true) })
        .catch((err) => {
          console.error('Failed to fetch expert earnings:', (err as any)?.response?.status, (err as any)?.message)
          setEarningsError(err?.response?.data?.message || 'Failed to load earnings.')
          // Marked loaded so the effect does not retry in a loop; switching
          // tabs away and back does not retry either — use the button.
          setEarningsLoaded(true)
        })
    }
    if (tab === 'documents' && !onboarding) {
      getExpertOnboardingStatus(id)
        .then((res) => setOnboarding(res.data.data))
        .catch((err) => console.error('Failed to fetch onboarding status:', (err as any)?.response?.status, (err as any)?.message))
    }
    if (tab === 'documents' && !documentsLoaded) {
      listExpertDocuments(id).then((res) => {
        // Company documents (registration, liability, commercial
        // auto) belong exclusively on CompaniesPage — see
        // excludeCompanyDocs above for why all three, not just one,
        // must be stripped here.
        setRawDocuments(res.data.data)
        setDocumentsLoaded(true)
      }).catch(() => { /* opening the tab will retry */ })
    }
    if (tab === 'insurance' && !insurancePoliciesLoaded) {
      listExpertInsurancePolicies(id)
        .then((res) => { setInsurancePolicies(res.data.data); setInsurancePoliciesLoaded(true) })
        .catch(() => { /* opening the tab will retry */ })
    }
  }, [tab, id, tasksLoaded, earningsLoaded, documentsLoaded, onboarding, insurancePoliciesLoaded])

  // Prefetch documents in the background (if the user hasn't
  // opened that tab yet) so the Documents tab badge — pending
  // review count — is accurate from the moment the page loads.
  useEffect(() => {
    if (!id || documentsLoaded) return
    listExpertDocuments(id).then((res) => {
      setRawDocuments(res.data.data)
      setDocumentsLoaded(true)
    })
      .catch(() => { /* silent — opening the tab will retry */ })
    // Prefetched alongside the documents so the tab badge can count
    // what actually needs an ADMIN's attention (submitted) rather than
    // lumping in items already rejected and waiting on the expert.
    getExpertOnboardingStatus(id)
      .then((res) => setOnboarding(res.data.data))
      .catch(() => { /* silent — opening the tab will retry */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Default a category/vehicle section open only if it has a
  // pending document — a fully-reviewed section stays collapsed
  // (nothing left to do there), while anything still needing
  // action is immediately visible without having to click around.
  // Runs once per fresh document load; manual toggles afterward
  // are preserved since this only fires when documentsLoaded
  // flips, not on every render.
  useEffect(() => {
    if (!documentsLoaded || !expert) return
    const VEHICLE_CATEGORY_GROUPS = new Set(['Moving & Hauling', 'Delivery', 'Food Delivery & Meal Services', 'Shopping & Errands'])
    const vehicleRequiringCategoryNames = new Set(
      (expert.categories || [])
        .filter((ec) => VEHICLE_CATEGORY_GROUPS.has(ec.category.group || ''))
        .map((ec) => ec.category.name)
    )
    const defaultOpen = new Set<string>()
    for (const doc of documents) {
      if (isPersonalDocType(doc.type)) continue
      // Only auto-open sections holding something an admin must act
      // on. `!isVerified` also opened sections whose only unverified
      // document was one we had already rejected — work that belongs
      // to the expert, not to us.
      if (docStatusOf(doc) !== 'submitted') continue
      if (doc.category) {
        defaultOpen.add(`cat:${doc.category.name}`)
      } else if (doc.vehicleType) {
        // Nested under every vehicle-requiring category's own
        // toggle now — matches how the render actually groups
        // vehicle documents (see the Documents tab render below).
        if (vehicleRequiringCategoryNames.size > 0) {
          vehicleRequiringCategoryNames.forEach((catName) => defaultOpen.add(`cat:${catName}`))
        } else {
          defaultOpen.add(`veh:${doc.vehicleType}`)
        }
      }
    }
    setExpandedDocSections(defaultOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsLoaded])

  const handleApproveExpert = async () => {
    if (!canModify || !id) return
    setApprovingExpert(true)
    try {
      await updateExpertStatus(id, { status: 'approved' })
      setExpert((prev) => prev ? { ...prev, status: 'approved' } : prev)
    } catch (err: any) {
      console.error('Failed to approve expert:', (err as any)?.response?.status, (err as any)?.message)
      alert(err.response?.data?.message || 'Failed to approve expert')
    } finally {
      setApprovingExpert(false)
    }
  }

  // Counterpart to the above — validateUpdateExpertStatus on the
  // backend requires a reason for 'rejected' (same as 'suspended'),
  // so this always goes through the reason modal below rather than
  // firing directly the way Approve does.
  const handleRejectExpert = async () => {
    if (!canModify || !id || !rejectExpertReason.trim()) return
    setRejectingExpert(true)
    try {
      await updateExpertStatus(id, { status: 'rejected', reason: rejectExpertReason.trim() })
      setExpert((prev) => prev ? { ...prev, status: 'rejected', rejectedReason: rejectExpertReason.trim() } : prev)
      setShowRejectExpert(false)
      setRejectExpertReason('')
    } catch (err: any) {
      console.error('Failed to reject expert:', (err as any)?.response?.status, (err as any)?.message)
      alert(err.response?.data?.message || 'Failed to reject expert')
    } finally {
      setRejectingExpert(false)
    }
  }

  const submitSubOverride = async () => {
    if (!canModify || !id) return
    setSavingSubOverride(true)
    try {
      const result = await overrideExpertSubscription(id, {
        subscriptionStatus: subOverrideStatus,
        subscriptionRenewsAt: subOverrideRenewsAt ? new Date(subOverrideRenewsAt).toISOString() : undefined,
        reason: subOverrideReason.trim() || undefined,
      })
      setExpert((prev) => prev ? {
        ...prev,
        subscriptionStatus: result.data.data.subscriptionStatus,
        subscriptionRenewsAt: result.data.data.subscriptionRenewsAt,
        pastDueSince: result.data.data.pastDueSince,
      } : prev)
      setShowSubOverride(false)
    } catch (err: any) {
      // FIX: this used to be a silent `void 0` with no alert and no
      // error state for this modal — a failed subscription override
      // gave the admin zero feedback at all. The modal just sat there
      // with "Save" re-enabled and no explanation, which reads as "the
      // button doesn't work" rather than "the write failed." Matches
      // the alert() pattern handleApproveExpert/handleRejectExpert
      // already use for other write actions on this page.
      console.error('Failed to override expert subscription:', (err as any)?.response?.status, (err as any)?.message)
      alert(err.response?.data?.message || 'Failed to update subscription — try again')
    } finally {
      setSavingSubOverride(false)
    }
  }

  const handleRemoveCategory = async (categoryId: string, categoryName: string) => {
    if (!canModify || !id) return
    if (!window.confirm(`Remove "${categoryName}" from this expert's profile? This will also permanently delete all documents they uploaded for this category.`)) return
    try {
      await removeExpertCategory(id, categoryId)
      // Refresh expert (category list), documents, and the resolver —
      // removing a category deletes its documents and changes which
      // licence requirements still apply, so the checklist moves too.
      // Same by-id endpoint as the initial load; this was the second
      // place pulling the whole expert list to find one row.
      const [expertRes, docsRes] = await Promise.all([
        getExpertById(id),
        listExpertDocuments(id),
      ])
      setExpert(expertRes.data.data)
      setRawDocuments(docsRes.data.data)
      getExpertOnboardingStatus(id)
        .then((res) => setOnboarding(res.data.data))
        .catch(() => { /* non-fatal — the panel keeps its last value */ })
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove category')
    }
  }

  // FIX — A FAILED APPROVE/REJECT WAS INVISIBLE.
  //
  // Two defects, both of which made this button look like it "did
  // nothing" when something had actually gone wrong:
  //
  // 1. The catch was console.error ONLY. Every other handler in this
  //    file (handleRemoveCategory, handleDeleteDocument,
  //    handleRemoveVehicle) alerts the server's message; this one — the
  //    single most-used action on the page — swallowed it. The spinner
  //    stopped, the row did not change, and the admin was given no
  //    reason at all. A 4xx from verifyDocument (a draft document, a
  //    document belonging to a different expert, a missing reason) was
  //    indistinguishable from a dead button.
  //
  // 2. The write and the two refreshes shared one try block, so a
  //    failure in EITHER refresh was reported as a failed review even
  //    though the review had already committed server-side. That is the
  //    worse of the two: the admin retries an action that already
  //    succeeded. getExpertOnboardingStatus re-runs the whole resolver
  //    and is the most likely of the three to fail, which made this a
  //    realistic path rather than a theoretical one.
  //
  // The write is now its own try/catch and is the only failure that
  // reports a failed review. The refreshes follow separately and
  // degrade quietly — the data on screen is stale, not wrong, and the
  // next navigation or manual refresh corrects it.
  // Re-reads the three things a document action can change. Shared by
  // the success path below and by the stale-id path, which needs the
  // same refresh for the opposite reason — nothing was written, and the
  // list on screen is what is wrong.
  const refreshDocumentData = async () => {
    if (!id) return
    const [expertRes, res] = await Promise.all([
      getExpertById(id),
      listExpertDocuments(id),
    ])
    setExpert(expertRes.data.data)
    setRawDocuments(res.data.data)
    const status = await getExpertOnboardingStatus(id)
    setOnboarding(status.data.data)
  }

  const handleVerifyDocument = async (
    docId: string,
    isVerified: boolean,
    reason?: string,
    insurancePolicy?: { insuranceType?: InsuranceType; provider: string; expiresAt: string | null }
  ): Promise<boolean> => {
    if (!canModify || !id) return false
    setActingDocId(docId)

    try {
      await verifyDocument(id, docId, isVerified, reason, insurancePolicy)
    } catch (err: any) {
      console.error('Failed to verify document:', (err as any)?.response?.status, (err as any)?.message)

      // FIX: 'Document not found' was surfaced as a bare browser alert
      // over a list that still showed the document, so the only way
      // forward was to reload the page by hand — and pressing the button
      // again produced the same 404 forever.
      //
      // It is not a broken button. Document ids are not stable across a
      // re-upload: expertDocument.service.js's uploadDocument deletes the
      // superseded row and creates a NEW one, with a new id, inside one
      // transaction. So any admin holding this page while the expert
      // re-uploads is holding an id that no longer exists. An admin
      // force-delete (adminDeleteDocument) or a category removal, which
      // deletes that category's documents, do the same thing.
      //
      // The right response is to re-read and say what happened, not to
      // repeat a request that cannot ever succeed. The modal is closed
      // here rather than left open with the typed reason, because that
      // reason was written about a file that is gone — the replacement
      // needs its own review, on its own merits.
      const isStale =
        err.response?.status === 404
        || /document not found/i.test(err.response?.data?.message || '')

      if (isStale) {
        setReuploadModal(null)
        setReuploadReason('')
        setInsuranceVerifyModal(null)
        try {
          await refreshDocumentData()
          alert('That document is no longer there — the expert most likely replaced it. The list has been refreshed; review the current version.')
        } catch (refreshErr) {
          console.error('Failed to refresh document list after a stale-document error:', (refreshErr as any)?.response?.status, (refreshErr as any)?.message)
          alert('That document is no longer there — the expert most likely replaced it. Reload the page to see the current documents.')
        }
        setActingDocId(null)
        return false
      }

      alert(
        err.response?.data?.message
        || (err.isNetworkError
          ? 'Could not reach the server — check your connection and try again.'
          : 'Failed to update document review')
      )
      setActingDocId(null)
      return false
    }

    // Committed server-side from here on. Re-read documents, the
    // resolver, AND the expert record itself.
    //
    // FIX: expert was never re-fetched here. A rejection can demote
    // Expert.status (see verifyDocument's demotion branch in
    // admin.service.js) — the status pill at the top of this page, and
    // the branches at ~line 1031/1052/1069 that key off expert.status,
    // all read the stale `expert` object and kept showing the OLD
    // status. The reject/approve had already succeeded server-side —
    // confirmed by a 200 response and by the expert's own app
    // immediately re-fetching its onboarding data — but this page gave
    // no visible sign anything happened, which read as "the button
    // doesn't work" when the actual failure was a missing refresh.
    // handleRemoveCategory and handleRemoveVehicle already call
    // getExpertById; this was the one handler missing it.
    try {
      await refreshDocumentData()
    } catch (err) {
      // Deliberately not an alert — the review DID succeed. Saying it
      // failed here is what would make an admin repeat it. Still
      // logged so a real pattern of refresh failures is visible
      // somewhere instead of being invisible everywhere.
      console.error('Failed to refresh document data after a successful review:', (err as any)?.response?.status, (err as any)?.message)
    } finally {
      setActingDocId(null)
    }

    // True means the WRITE succeeded — a failed refresh above does not
    // make the review unsuccessful, and callers must not treat it as one.
    return true
  }

  // FIX: the modal closed unconditionally. handleVerifyDocument swallows
  // nothing now, but it still returns normally after a failed write — so
  // this closed the modal and discarded the typed reason even when the
  // rejection had not been recorded. The admin saw the modal dismiss,
  // read that as success, and moved on. It now closes only on success,
  // keeping the reason in the box to retry.
  const submitReupload = async () => {
    if (!reuploadModal || !reuploadReason.trim()) return
    const ok = await handleVerifyDocument(reuploadModal.id, false, reuploadReason.trim())
    if (!ok) return
    setReuploadModal(null)
    setReuploadReason('')
  }

  // Force-delete — distinct from reject. Use when a document is
  // duplicate, orphaned (its category/vehicle no longer exists), or
  // otherwise bad data an admin wants gone rather than reviewed.
  const handleDeleteDocument = async (doc: AdminExpertDocument) => {
    if (!canModify || !id) return
    if (!window.confirm(`Permanently delete "${doc.fileName || doc.type}"? This cannot be undone.`)) return
    setActingDocId(doc.id)
    try {
      await deleteExpertDocument(id, doc.id)
      const res = await listExpertDocuments(id)
      setRawDocuments(res.data.data)
      const status = await getExpertOnboardingStatus(id)
      setOnboarding(status.data.data)
    } catch (err: any) {
      // Same stale-id case as handleVerifyDocument above — deleting a row
      // the expert already replaced 404s, and the outcome the admin
      // wanted (that file gone) is already true. Refresh and say so
      // rather than reporting a failure.
      const isStale =
        err.response?.status === 404
        || /document not found/i.test(err.response?.data?.message || '')
      if (isStale) {
        try { await refreshDocumentData() } catch { /* stale list, next load corrects it */ }
        alert('That document is no longer there — it was already removed or replaced. The list has been refreshed.')
        setActingDocId(null)
        return
      }
      alert(err.response?.data?.message || 'Failed to delete document')
    } finally {
      setActingDocId(null)
    }
  }

  // Removes a vehicle and every document scoped to it. Distinct from
  // handleRemoveCategory — vehicles have no relation to ExpertCategory
  // at all in the data model (see admin.service.js's removeExpertVehicle
  // header comment), so this is its own action with its own endpoint.
  const handleRemoveVehicle = async (vehicleType: string) => {
    if (!canModify || !id) return
    if (!window.confirm(`Remove this ${vehicleType} and permanently delete all its documents?`)) return
    try {
      await removeExpertVehicle(id, vehicleType)
      const [expertRes, docsRes] = await Promise.all([
        getExpertById(id),
        listExpertDocuments(id),
      ])
      setExpert(expertRes.data.data)
      setRawDocuments(docsRes.data.data)
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove vehicle')
    }
  }

  // Replaces `setPreviewDoc(doc)` as the entry point for "Preview
  // File →". Fetches a fresh signed link on every open rather than
  // reusing doc.fileUrl — see the state declarations above and
  // admin.api.ts's getDocumentViewUrlApi for why: the backend writes
  // an audit log entry for this specific view before it will hand
  // back a link at all, and that has to happen every time a document
  // is actually opened, not once when the list loaded.
  const openDocumentPreview = async (doc: AdminExpertDocument) => {
    if (!id) return
    setPreviewDoc(doc)
    setPreviewUrl(null)
    setPreviewError('')
    setPreviewMfaRequired(false)
    setPreviewLoading(true)
    try {
      const res = await getDocumentViewUrl(id, doc.id)
      // FIX: the backend (admin.service.js's getDocumentViewUrl) returns
      // { viewUrl, expiresInMs, fileName } — this read `.url`, which
      // doesn't exist, so previewUrl was always undefined and the modal
      // rendered an empty body for every image and PDF with no error.
      const link = res.data?.data?.viewUrl
      if (!link) throw new Error('No view link was returned for this document.')
      setPreviewUrl(link)
    } catch (err: any) {
      // 401 here specifically means "no authenticator-app second
      // factor on this sign-in" (see requireMfa.middleware.js on the
      // backend) — every other admin route still works normally, only
      // this audited view is gated. Worth its own message rather than
      // the generic fallback, since "try again" would just fail again.
      // FIX: the view link also has a 12-hour recent-sign-in check
      // (requireRecentAuth(720)), which is a 401 too — it used to get
      // the authenticator-app message. Neither 401 signs the admin out
      // any more (config/api.ts), so these messages are now reachable.
      if (isReauthRequired(err)) {
        setPreviewMfaRequired(true)
        setPreviewError('For security, viewing documents needs a sign-in from the last 12 hours. Sign out and sign in again, then reopen this document.')
      } else if (err?.response?.status === 401) {
        setPreviewMfaRequired(true)
        setPreviewError('Viewing this document requires signing in with your authenticator app. Set it up in Settings, then sign out and back in.')
      } else {
        setPreviewError(err?.response?.data?.message || (err?.isNetworkError
          ? 'Could not reach the server — check your connection and try again.'
          : 'Could not open this document — try again.'))
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const closeDocumentPreview = () => {
    setPreviewDoc(null)
    setPreviewUrl(null)
    setPreviewError('')
    setPreviewMfaRequired(false)
  }

  const openTaskDetail = async (taskId: string) => {
    try {
      const res = await getTaskDetail(taskId)
      setSelectedTask(res.data.data)
    } catch (err) {
      console.error('Failed to fetch task detail:', (err as any)?.response?.status, (err as any)?.message)
    }
  }

  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  const copyExpertId = () => {
    if (!id) return
    navigator.clipboard?.writeText(id).then(() => {
      setIdCopied(true)
      setTimeout(() => setIdCopied(false), 1500)
    })
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading...</div></div>
  // A failed request and a genuine 404 both used to render as
  // "Expert not found", which sent admins looking for a data problem
  // that was actually a dropped connection.
  if (loadError) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4">
        <p className="text-red-400 text-sm">{loadError}</p>
      </div>
    )
  }
  if (!expert) return <div className="text-gray-400">Expert not found</div>

  const isCompany = (expert as any).expertType === 'company'
  // Counts what an ADMIN still has to act on. `!isVerified` counted
  // rejected documents too — which are waiting on the EXPERT, not on
  // us — so the badge overstated the queue and never went down until
  // the expert re-uploaded.
  const pendingDocCount = documents.filter((d) => docStatusOf(d) === 'submitted').length
  const catCount = expert.categories?.length || 0
  const catCertifiedCount = expert.categories?.filter((c) => c.isCertified).length || 0
  const jobsCompletedCount = isCompany
    ? (expert as any).totalJobsAsCompany ?? 0
    : (expert as any).totalJobsCompleted ?? 0

  return (
    <div>
      <button onClick={() => navigate('/experts')} className="flex items-center gap-1.5 text-sm text-brand-400 hover:text-brand-300 mb-4">
        <ArrowLeft size={15} /> Back to Experts
      </button>

      {/* ── Summary header card ─────────────────────────────── */}
      <div className="bg-surface-card border border-border-light rounded-2xl p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-purple-700 flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {expert.user?.firstName?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-bold text-white">{expert.user?.firstName} {expert.user?.lastName}</h1>
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${EXPERT_STATUS_COLORS[expert.status] || 'bg-gray-600/20 text-gray-400'}`}>
                  {expert.status.replace(/_/g, ' ')}
                </span>
                {isCompany && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-600/20 text-indigo-400 flex items-center gap-1">
                    <Building2 size={12} /> Company
                  </span>
                )}
                {/* Tasker's own GST/HST declaration: decides whether customers are charged
                    tax on this tasker's job price. Shown here, not editable by admin. */}
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${(expert as any).taxRegistered ? 'bg-emerald-600/20 text-emerald-400' : 'bg-gray-600/20 text-gray-400'}`}
                  title={(expert as any).taxRegistered ? 'Tasker declared GST/HST registration — customers are charged tax on their job price' : 'No GST/HST declared — no tax on this tasker\'s job price'}>
                  {(expert as any).taxRegistered ? `GST/HST${(expert as any).gstHstNumber ? ` · ${(expert as any).gstHstNumber}` : ''}` : 'No GST/HST'}
                </span>
                {expert.subscriptionStatus === 'past_due' && (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-red-600/20 text-red-400">
                    ⚠ Subscription past due
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm mt-1">{expert.user?.email || expert.user?.phone || '—'}</p>
              <SupportRequestsBadge userId={expert.userId} appType="expert" />
              <SupportMessageButton userId={expert.userId} name={`${expert.user?.firstName || ''} ${expert.user?.lastName || ''}`.trim()} appType="expert" />
              <button onClick={copyExpertId} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 mt-1.5 transition-colors">
                {idCopied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
                {idCopied ? 'Copied' : id}
              </button>
            </div>
          </div>

          {/* Quick actions — surfaced here instead of buried in tabs.
              FIX (production-readiness pass): gated behind canModify —
              see the note on this component's declaration. The
              handlers themselves also refuse (defense in depth), but
              a customer_service viewer should never see live-looking
              Approve/Reject/Override buttons that silently no-op. */}
          {canModify ? (
            <div className="flex gap-2 shrink-0">
              {expert.status !== 'approved' && (
                <button onClick={handleApproveExpert} disabled={approvingExpert}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-500 disabled:opacity-50 transition-colors">
                  {approvingExpert ? 'Approving...' : 'Approve Expert'}
                </button>
              )}
              {!['rejected', 'suspended'].includes(expert.status) && (
                <button onClick={() => { setShowRejectExpert(true); setRejectExpertReason('') }}
                  className="px-4 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 transition-colors">
                  Reject Expert
                </button>
              )}
              <button
                onClick={() => {
                  setSubOverrideStatus((expert.subscriptionStatus as any) || 'active')
                  setSubOverrideRenewsAt(expert.subscriptionRenewsAt ? expert.subscriptionRenewsAt.slice(0, 10) : '')
                  setSubOverrideReason('')
                  setShowSubOverride(true)
                }}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors"
              >
                Override Subscription
              </button>
            </div>
          ) : (
            <span className="px-3 py-1.5 text-xs rounded-full bg-yellow-600/20 text-yellow-400 shrink-0">View Only</span>
          )}
        </div>

        {/* Quick stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-border-light">
          <div>
            <p className="text-lg font-semibold text-white">{jobsCompletedCount}</p>
            <p className="text-xs text-gray-500">{isCompany ? 'Jobs Dispatched' : 'Jobs Completed'}</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-white">{catCount === 0 ? '—' : `${catCertifiedCount}/${catCount}`}</p>
            <p className="text-xs text-gray-500">Categories Certified</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-white capitalize">{(expert.backgroundCheckStatus || 'not started').replace(/_/g, ' ')}</p>
            <p className="text-xs text-gray-500">Background Check</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-white">{new Date(expert.createdAt).toLocaleDateString()}</p>
            <p className="text-xs text-gray-500">Joined</p>
          </div>
          <div>
            {/* isOnline: manual toggle — tasker sets when ready */}
            <p className="text-lg font-semibold">
              {(expert as any).isOnline
                ? <span className="text-green-400">🟢 Online</span>
                : <span className="text-gray-500">⚫ Offline</span>
              }
            </p>
            <p className="text-xs text-gray-500">Availability</p>
          </div>
          <div>
            {/* lastSeenAt: updated on every app API call — more honest than
                isOnline alone. Shows engagement even when not toggled on. */}
            <p className="text-lg font-semibold text-white">
              {(expert as any).lastSeenAt
                ? (() => {
                    const diff  = Date.now() - new Date((expert as any).lastSeenAt).getTime()
                    const mins  = Math.floor(diff / 60000)
                    const hours = Math.floor(diff / 3600000)
                    const days  = Math.floor(diff / 86400000)
                    if (mins < 5)   return '< 5 min ago'
                    if (hours < 1)  return `${mins}m ago`
                    if (hours < 24) return `${hours}h ago`
                    return `${days}d ago`
                  })()
                : '—'
              }
            </p>
            <p className="text-xs text-gray-500">Last Seen</p>
          </div>
        </div>
      </div>

      {/* Needs Action banner — a decision an admin owes this expert
          RIGHT NOW (pending approval, documents submitted awaiting
          certification). Deliberately separate from, and shown above,
          the Issues panel below: nothing here is wrong — it's just
          computed live off expert.needsAction (see admin.service.js's
          buildExpertNeedsAction), so there's no "Clear" button and
          nothing to dismiss. The moment the decision is made (approve/
          reject, certify), the item is gone on the next load — no
          action needed here to make it disappear. */}
      {expert.needsAction && expert.needsAction.length > 0 && (
        <div className="mb-4 space-y-2">
          {expert.needsAction.map((item) => (
            <div key={item.code}
              className="rounded-xl p-3 text-sm flex items-start gap-2 bg-brand-500/10 border border-brand-500/30 text-brand-300">
              <span>🔔</span>
              <span>{item.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Issues panel — every blocking or warning condition currently
          on this account, in one place, regardless of which tab is
          open. See admin.service.js's buildExpertIssues for what's
          checked (account status, background check, rejected
          documents, subscription, failed payouts, open disputes). */}
      {issueActionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-2 text-red-400 text-sm flex items-center justify-between gap-3">
          {issueActionError}
          <button onClick={() => setIssueActionError('')} className="text-red-300 hover:text-white shrink-0">✕</button>
        </div>
      )}
      {(expert as any).issues && (expert as any).issues.length > 0 && (
        <div className="mb-6 space-y-2">
          {(expert as any).issues.map((issue: any) => (
            <div key={issue.code}
              className={`rounded-xl p-3 text-sm flex items-start justify-between gap-3 ${
                issue.severity === 'blocking'
                  ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                  : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400'
              }`}>
              <span className="flex items-start gap-2">
                <span>{issue.severity === 'blocking' ? '⛔' : '⚠️'}</span>
                <span>{issue.message}</span>
              </span>
              <button
                onClick={() => handleDismissIssue(issue.code)}
                disabled={dismissingIssue === issue.code}
                title="Mark as handled — reappears automatically if this happens again"
                className="text-xs px-2 py-1 rounded-lg bg-black/20 hover:bg-black/30 disabled:opacity-50 transition-colors shrink-0 whitespace-nowrap">
                {dismissingIssue === issue.code ? 'Clearing…' : 'Clear'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={toggleShowDismissed}
          className="text-xs text-gray-400 hover:text-white transition-colors underline decoration-dotted underline-offset-2">
          {showDismissed ? 'Hide dismissed issues' : 'Show dismissed issues'}
        </button>
        {showDismissed && (
          <div className="mt-2 space-y-2">
            {dismissedLoading && (
              <div className="text-sm text-gray-500">Loading dismissed issues…</div>
            )}
            {!dismissedLoading && dismissedIssues && dismissedIssues.length === 0 && (
              <div className="text-sm text-gray-500">No dismissed issues.</div>
            )}
            {!dismissedLoading && dismissedIssues && dismissedIssues.map((d) => (
              <div key={d.code}
                className="rounded-xl p-3 text-sm flex items-start justify-between gap-3 bg-surface-tertiary border border-border-light text-gray-300">
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <span>{d.label}</span>
                    {d.newOccurrenceSince && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400">
                        New occurrence since dismissal
                      </span>
                    )}
                    {d.stillSuppressing && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-black/20 text-gray-400">
                        Currently hidden
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500">
                    Dismissed {new Date(d.dismissedAt).toLocaleString()}
                  </span>
                </span>
                <button
                  onClick={() => handleRestoreIssue(d.code)}
                  disabled={restoringIssue === d.code}
                  title="Bring this issue back so it shows in the panel above"
                  className="text-xs px-2 py-1 rounded-lg bg-black/20 hover:bg-black/30 disabled:opacity-50 transition-colors shrink-0 whitespace-nowrap">
                  {restoringIssue === d.code ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Tab nav ──────────────────────────────────────────── */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {(['documents', 'categories', 'profile', 'jobs', 'earnings', 'insurance', 'activity'] as Tab[]).map((t) => {
          const Icon = TAB_ICONS[t]
          const badge = t === 'documents' && pendingDocCount > 0 ? pendingDocCount : null
          return (
            <button key={t} onClick={() => setTab(t)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl transition-colors whitespace-nowrap ${
                tab === t ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'
              }`}>
              <Icon size={15} />
              {TAB_LABELS[t]}
              {badge != null && (
                <span className={`px-1.5 py-0.5 text-[11px] rounded-full ${tab === t ? 'bg-white/20 text-white' : 'bg-yellow-600/30 text-yellow-400'}`}>
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {tab === 'profile' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SectionCard title="Contact">
            <DetailRow label="Phone" value={expert.user?.phone || '—'} />
            <DetailRow label="Email" value={expert.user?.email || '—'} />
            <DetailRow label="Home Address" value={expert.address || 'Not set'} />
            {/* 2026-10-10: the work address was never shown here. It is where
                the tasker works from (distances use it) and every individual
                — including a Company-path applicant before approval — must
                give one. An approved company uses its business address
                instead (Company card). */}
            {!isCompany && (
              <DetailRow
                label="Work Address"
                value={
                  (expert as any).workSameAsHome === true
                    ? `Same as home address${expert.address ? '' : ' (home address not set)'}`
                    : ((expert as any).workAddress || 'Not set')
                }
              />
            )}
            <DetailRow label="Bio" value={expert.bio || 'Not set'} />
            {expert.rejectedReason && <DetailRow label="Rejected Reason" value={expert.rejectedReason} />}
            {expert.suspendedReason && <DetailRow label="Suspended Reason" value={expert.suspendedReason} />}
          </SectionCard>

          {/* Option A: chose the company track at signup but hasn't been
              approved yet — expertType is still 'individual', so `isCompany`
              below is false and the Company card doesn't render. Without
              this, admin reviewing this applicant had no way to know a
              company registration is coming; see admin.service.js's
              applyPendingCompanyConversion, which converts expertType to
              'company' automatically the instant this expert is approved,
              using exactly the fields saved here. */}
          {!isCompany && (expert as any).pendingCompanyConversion && (
            <SectionCard title="Company Registration (Pending Approval)">
              <DetailRow label="Account Type" value="Individual — will switch to Company on approval" />
              {(expert as any).businessName && <DetailRow label="Business Name" value={(expert as any).businessName} />}
              {(expert as any).businessNumber && <DetailRow label="Business Number" value={(expert as any).businessNumber} />}
              {(expert as any).businessType && (
                <DetailRow
                  label="Business Type"
                  value={
                    ({ sole_proprietor: 'Sole Proprietor', incorporated: 'Incorporated', partnership: 'Partnership', other: 'Other' } as Record<string, string>)
                      [(expert as any).businessType] || (expert as any).businessType
                  }
                />
              )}
              {/* The business address is entered in Company Profile, which the
                  tasker gets only once the account is switched to a company at
                  approval. Until then the work address (Contact card) is used. */}
              <DetailRow
                label="Business Address"
                value={(expert as any).businessAddress || 'Added after approval (Company Profile) — work address is used until then'}
              />
            </SectionCard>
          )}

          {isCompany && (
            <SectionCard title="Company">
              <DetailRow label="Account Type" value="Company" />
              {(expert as any).businessName && <DetailRow label="Business Name" value={(expert as any).businessName} />}
              {(expert as any).businessNumber && <DetailRow label="Business Number" value={(expert as any).businessNumber} />}
              {/* FIX — EVERYTHING BELOW WAS COLLECTED BY
                  CompanyProfileScreen.tsx AND SAVED CORRECTLY (see
                  expert.service.js's updateCompanyProfile and the
                  Expert model's businessAddress/businessPhone/etc.
                  columns — nothing wrong on the backend), but never
                  actually rendered here, so an admin reviewing a
                  company had no way to see any of it without
                  querying the database directly. */}
              {(expert as any).businessType && (
                <DetailRow
                  label="Business Type"
                  value={
                    ({ sole_proprietor: 'Sole Proprietor', incorporated: 'Incorporated', partnership: 'Partnership', other: 'Other' } as Record<string, string>)
                      [(expert as any).businessType] || (expert as any).businessType
                  }
                />
              )}
              {(expert as any).businessAddress && <DetailRow label="Business Address" value={(expert as any).businessAddress} />}
              {(expert as any).businessPhone && <DetailRow label="Business Phone" value={(expert as any).businessPhone} />}
              {(expert as any).businessEmail && (
                <DetailRow
                  label="Business Email"
                  value={`${(expert as any).businessEmail}${(expert as any).businessEmailVerified ? ' (Verified)' : ' (Unverified)'}`}
                />
              )}
              {(expert as any).businessWebsite && <DetailRow label="Business Website" value={(expert as any).businessWebsite} />}
              {(expert as any).authorizedRepName && <DetailRow label="Authorized Representative" value={(expert as any).authorizedRepName} />}
              {(expert as any).authorizedRepTitle && <DetailRow label="Representative Title" value={(expert as any).authorizedRepTitle} />}
              {(expert as any).insuranceProvider && <DetailRow label="Insurance Provider" value={(expert as any).insuranceProvider} />}
              {(expert as any).insurancePolicyNumber && <DetailRow label="Insurance Policy #" value={(expert as any).insurancePolicyNumber} />}
              {(expert as any).taxNumber && <DetailRow label="Tax Number" value={(expert as any).taxNumber} />}
              <DetailRow label="Jobs Dispatched" value={`${(expert as any).totalJobsAsCompany ?? 0} total`} />
            </SectionCard>
          )}

          {/* GST/HST declaration: the tasker's own answer, plus every earlier answer. */}
          <SectionCard title="GST/HST declaration">
            <DetailRow label="Declared status" value={(expert as any).taxDeclaredAt || (expert as any).taxRegistered
              ? ((expert as any).taxRegistered ? 'Registered' : 'Not registered (confirmed not required)')
              : 'Not declared yet'} />
            {(expert as any).taxRegistered && (
              <DetailRow label="Pricing" value={(expert as any).taxPricingMode === 'TAX_INCLUDED' ? 'Tax included in the tasker\'s price' : 'Tax added on top of the tasker\'s price'} />
            )}
            {(expert as any).taxRegistered && <DetailRow label="GST/HST number" value={(expert as any).gstHstNumber || '—'} />}
            {(expert as any).taxRegistered && (expert as any).taxEffectiveDate && (
              <DetailRow label="Registration effective" value={new Date((expert as any).taxEffectiveDate).toLocaleDateString()} />
            )}
            {(expert as any).taxDeclaredAt && (
              <DetailRow label="Last declared" value={new Date((expert as any).taxDeclaredAt).toLocaleString()} />
            )}
            <p className="text-xs text-gray-500 mt-2">Declared by the tasker; MatchNBook has not verified it with the CRA.</p>
            {Array.isArray((expert as any).taxDeclarations) && (expert as any).taxDeclarations.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-gray-500 uppercase mb-2">History</p>
                <div className="space-y-2">
                  {(expert as any).taxDeclarations.map((d: any) => (
                    <div key={d.id} className="text-xs text-gray-300 bg-surface-tertiary rounded-lg px-3 py-2">
                      <p>
                        <span className="text-gray-400">{new Date(d.declaredAt).toLocaleString()}</span>
                        {' · '}
                        {d.registered ? `Registered${d.gstHstNumber ? ` (${d.gstHstNumber})` : ''}` : 'Not registered'}
                        {d.registered && d.effectiveDate ? ` · effective ${new Date(d.effectiveDate).toLocaleDateString()}` : ''}
                        {d.registered && d.taxPricingMode ? (d.taxPricingMode === 'TAX_INCLUDED' ? ' · tax included in price' : ' · tax added to price') : ''}
                      </p>
                      <p className="text-gray-500">
                        {d.previousRegistered == null
                          ? 'First declaration'
                          : `Changed from ${d.previousRegistered ? `registered${d.previousGstHstNumber ? ` (${d.previousGstHstNumber})` : ''}` : 'not registered'}`}
                        {' · wording '}{d.declarationVersion}{d.source !== 'tasker' ? ` · by ${d.source}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          {!isCompany && (expert as any).totalJobsCompleted != null && (
            <SectionCard title="Activity">
              <DetailRow label="Jobs Completed" value={`${(expert as any).totalJobsCompleted} total`} />
            </SectionCard>
          )}

          <SectionCard title="Subscription & Stripe">
            <div className="flex justify-between py-1.5 border-b border-border-light">
              <p className="text-xs text-gray-500">Subscription</p>
              <p className={`text-sm text-right max-w-xs ${expert.subscriptionStatus === 'past_due' ? 'text-red-400 font-medium' : 'text-gray-300'}`}>
                {expert.subscriptionStatus === 'past_due' && '⚠️ '}
                {(expert.subscriptionStatus || 'free').replace('_', ' ')}
                {expert.subscriptionRenewsAt && ` — ${expert.subscriptionStatus === 'past_due' ? 'due' : 'renews'} ${new Date(expert.subscriptionRenewsAt).toLocaleDateString()}`}
                {expert.subscriptionStatus === 'past_due' && expert.pastDueSince && ` (${Math.floor((Date.now() - new Date(expert.pastDueSince).getTime()) / (1000 * 60 * 60 * 24))}d)`}
              </p>
            </div>
            {(() => {
              // Falls back to the old yes/no flag for rows that were never synced.
              const key = expert.stripeBankStatus || (expert.stripeOnboardingComplete ? 'verified' : 'not_started')
              const info = STRIPE_STATUS_INFO[key] || { label: key.replace(/_/g, ' '), className: 'text-gray-300' }
              const due = expert.stripeRequirementsDue || []
              return (
                <>
                  <div className="flex justify-between py-1.5 border-b border-border-light">
                    <p className="text-xs text-gray-500">Stripe Setup</p>
                    <p className={`text-sm text-right max-w-xs ${info.className}`}>{info.label}</p>
                  </div>
                  {due.length > 0 && (
                    <DetailRow label="Stripe Still Needs" value={due.join(', ')} />
                  )}
                  {expert.stripeStatusCheckedAt && (
                    <DetailRow label="Last Checked" value={new Date(expert.stripeStatusCheckedAt).toLocaleString()} />
                  )}
                </>
              )
            })()}
            <p className="text-xs text-gray-600 pt-2">
              Payments go straight into the tasker’s own Stripe account when a job is confirmed. When Stripe pays out to their bank is their own Stripe setting — it isn’t visible or changeable here.
            </p>
            <p className="text-xs text-gray-600 pt-2">Rate, experience, and certification are set per category — see the Categories tab.</p>
          </SectionCard>

          {/* What happened after approval: Stripe / payout steps and the GST/HST answer, with dates. */}
          {id && (expert.status === 'approved' || (expert as any).approvedAt) && <SetupTimelineCard expertId={id} />}
        </div>
      )}

      {tab === 'categories' && (() => {
        // FIX: this tab previously showed only name/rate/experience/
        // certified — none of the OTHER things an admin needs to see
        // at a glance about a category the expert added: which
        // vehicle they registered for it (if the category requires
        // one), that vehicle's own approval status, and how many of
        // the category's documents are verified vs. still pending —
        // all of which lived only on the Documents tab, disconnected
        // from the category card itself.
        //
        // Same VEHICLE_CATEGORY_GROUPS set already used elsewhere in
        // this file (defaultOpen effect, Documents tab nesting) — a
        // category's group name is what determines whether a vehicle
        // applies to it at all, not any property on ExpertCategory.
        const VEHICLE_CATEGORY_GROUPS = new Set(['Moving & Hauling', 'Delivery', 'Food Delivery & Meal Services', 'Shopping & Errands'])

        // Every document belonging to this category — both direct
        // category docs (certificates) and licence-requirement docs,
        // which also carry categoryId (see expertDocument.service.js's
        // uploadDocument — categoryId is stored alongside
        // licenseRequirementId, not instead of it).
        const docsForCategory = (categoryId: string) =>
          documents.filter((d) => (d.category?.id ?? (d as any).categoryId) === categoryId)

        const VEHICLE_STATUS_LABEL: Record<string, string> = {
          approved: '✓ Vehicle Approved', pending: 'Vehicle Pending', rejected: 'Vehicle Rejected',
        }
        const VEHICLE_STATUS_CLASS: Record<string, string> = {
          approved: 'bg-green-600/20 text-green-400',
          pending:  'bg-yellow-600/20 text-yellow-400',
          rejected: 'bg-red-600/20 text-red-400',
        }
        const VEHICLE_TYPE_ICON: Record<string, string> = {
          car: '🚗', van: '🚐', truck: '🚚', motorcycle: '🏍️',
          bicycle: '🚲', ebike: '🚲', moped: '🛵', walking: '🚶',
        }

        return (
        <div>
          {(!expert.categories || expert.categories.length === 0) ? (
            <p className="text-center py-8 text-gray-400 bg-surface-card border border-border-light rounded-2xl">No categories selected yet</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {expert.categories.map((ec) => {
                const requiresVehicle = VEHICLE_CATEGORY_GROUPS.has(ec.category.group || '')
                // The data model doesn't tie a specific vehicle to a
                // specific category — an expert has one vehicle list,
                // period. Same assumption the Documents tab nesting
                // already makes: if this category needs a vehicle at
                // all, show whichever vehicle(s) the expert has on
                // file, since in practice there's exactly one.
                const relevantVehicles = requiresVehicle ? ((expert as any).vehicles || []) : []
                const catDocs = docsForCategory(ec.category.id)
                const catTally = tallyReviewStatus(catDocs)
                return (
                  <div key={ec.id} className="bg-surface-card border border-border-light rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-base font-semibold text-white">{ec.category.name}</p>
                      <span className={`px-2 py-1 text-xs rounded-full ${ec.isCertified ? 'bg-green-600/20 text-green-400' : 'bg-gray-600/20 text-gray-400'}`}>
                        {ec.isCertified ? '✓ Certified' : 'Not Certified'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <DetailRow label="Rate" value={ec.rate != null ? `$${ec.rate}/hr` : 'Not set'} />
                      <DetailRow label="Experience" value={ec.yearsExperience != null ? `${ec.yearsExperience} years` : 'Not set'} />
                    </div>
                    {ec.isCertified && ec.certifiedAt && (
                      <p className="text-xs text-gray-500 mt-2">Certified on {new Date(ec.certifiedAt).toLocaleDateString()}</p>
                    )}

                    {/* ── Vehicle — only shown for categories that
                         actually require one ─────────────────── */}
                    {requiresVehicle && (
                      <div className="mt-3 pt-3 border-t border-border-light">
                        {relevantVehicles.length === 0 ? (
                          <p className="text-xs text-gray-500">No vehicle registered yet</p>
                        ) : relevantVehicles.map((v: any) => (
                          <div key={v.id} className="flex items-center justify-between">
                            <p className="text-xs text-gray-400">
                              {VEHICLE_TYPE_ICON[v.vehicleType] || '🚗'}{' '}
                              {v.vehicleType.charAt(0).toUpperCase() + v.vehicleType.slice(1)}
                            </p>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${VEHICLE_STATUS_CLASS[v.verificationStatus] || 'bg-gray-600/20 text-gray-400'}`}>
                              {VEHICLE_STATUS_LABEL[v.verificationStatus] || v.verificationStatus}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* ── Document review summary ─────────────── */}
                    <div className="mt-3 pt-3 border-t border-border-light flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        {catDocs.length === 0 ? 'No documents uploaded' : `${catDocs.length} document${catDocs.length === 1 ? '' : 's'}`}
                      </p>
                      {catDocs.length > 0 && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${catTally.rejected > 0 ? 'bg-red-600/20 text-red-400' : catTally.pending > 0 ? 'bg-yellow-600/20 text-yellow-400' : 'bg-green-600/20 text-green-400'}`}>
                          {reviewSummaryLabel(catTally)}
                        </span>
                      )}
                    </div>

                    <button
                      onClick={() => { setTab('documents'); setDocSubTab('categories'); setExpandedDocSections((prev) => new Set(prev).add(`cat:${ec.category.name}`)) }}
                      className="mt-3 text-xs text-brand-400 hover:text-brand-300"
                    >
                      View documents →
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        )
      })()}

      {/* Every action this tasker took, plus everything that happened on
          jobs they were hired for or assigned to — with time and, for
          arrivals, where (distance to the address, verified, map).
          Clicking a task name opens it under Assigned Jobs. */}
      {/* expertRecordId read into a plain string first — same reason as
          on CustomerDetailPage: the null check must reach the arrow
          function for a strict (CI) type check to pass. */}
      {tab === 'activity' && (() => {
        const expertRecordId = id
        if (!expertRecordId) return null
        return (
          <div className="bg-surface-card border border-border-light rounded-2xl p-6">
            <TimelineSection
              title="Activity across all jobs (newest first)"
              fetcher={() => getExpertTimeline(expertRecordId)}
              showTask
              onOpenTask={(taskId) => { setTab('jobs'); openTaskDetail(taskId) }}
              reloadKey={expertRecordId}
            />
          </div>
        )
      })()}

      {tab === 'jobs' && (
        <div className="flex gap-6">
          <div className={`${selectedTask ? 'flex-1' : 'w-full'}`}>
            {tasksLoaded && tasks.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-surface-card border border-border-light rounded-2xl p-4">
                  <p className="text-lg font-semibold text-white">{tasks.length}</p>
                  <p className="text-xs text-gray-500">Total Jobs</p>
                </div>
                <div className="bg-surface-card border border-border-light rounded-2xl p-4">
                  <p className="text-lg font-semibold text-white">{tasks.filter((t: any) => t.status === 'completed').length}</p>
                  <p className="text-xs text-gray-500">Completed</p>
                </div>
                <div className="bg-surface-card border border-border-light rounded-2xl p-4">
                  <p className="text-lg font-semibold text-green-400">
                    ${tasks.reduce((sum: number, t: any) => sum + (t.taskerPayout != null ? t.taskerPayout : 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Total Earned (Net)</p>
                </div>
              </div>
            )}

            <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
              {tasks.length === 0 ? (
                <p className="text-center py-8 text-gray-400">No assigned jobs yet</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-light">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t: any) => (
                      <tr key={t.id} onClick={() => openTaskDetail(t.id)}
                        className={`border-b border-border-light cursor-pointer hover:bg-surface-tertiary/50 transition-colors ${selectedTask?.id === t.id ? 'bg-surface-tertiary/70' : ''}`}>
                        <td className="px-5 py-4">
                          <p className="text-sm font-medium text-white">{t.title}</p>
                          <p className="text-xs text-gray-500">{t.category?.name}</p>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${TASK_STATUS_COLORS[t.status] || 'bg-gray-600/20 text-gray-400'}`}>{t.status.replace('_', ' ')}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-green-400">{t.taskerPayout != null ? `$${t.taskerPayout.toFixed(2)}` : t.finalPrice != null ? `$${t.finalPrice.toFixed(2)} (est.)` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {selectedTask && (
            <TaskDetailPanel task={selectedTask} onClose={() => setSelectedTask(null)} />
          )}
        </div>
      )}

      {tab === 'earnings' && (
        <div>
          {earningsError ? (
            <div>
              <p className="text-red-400 mb-2">{earningsError}</p>
              <button
                onClick={() => { setEarningsError(''); setEarningsLoaded(false) }}
                className="px-3 py-1.5 text-sm bg-surface-tertiary text-gray-300 hover:text-white rounded-xl transition-colors"
              >
                Try again
              </button>
            </div>
          ) : !earnings ? (
            <p className="text-gray-400">Loading earnings...</p>
          ) : (
            <>
              <a href={`/reports?scope=expert&id=${id}`} className="inline-block mb-4 text-sm text-brand-400 hover:text-brand-300">
                📈 View Detailed Report (daily/weekly/monthly/yearly) →
              </a>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
                <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                  <p className="text-2xl font-bold text-white">${(earnings.totalEarned ?? 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">Total Earned</p>
                  <p className="text-xs text-gray-600 mt-0.5">Completed jobs, after commission and fees</p>
                </div>
                <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                  <p className="text-2xl font-bold text-white">${(earnings.availableBalance ?? 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">Paid into their Stripe account</p>
                  <p className="text-xs text-gray-600 mt-0.5">The customer’s card was charged</p>
                </div>
                <div className="bg-surface-card border border-border-light rounded-2xl p-5">
                  <p className="text-2xl font-bold text-white">${(earnings.pendingBalance ?? 0).toFixed(2)}</p>
                  <p className="text-sm text-gray-400 mt-1">Not collected yet</p>
                  <p className="text-xs text-gray-600 mt-0.5">Completed, but the card charge hasn’t gone through (being retried)</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mb-6">
                Money goes straight from the customer’s card into the tasker’s own Stripe account when the job is confirmed; the platform keeps its commission as a fee. Jobs still on hold are not counted here. Payouts from Stripe to their bank follow the tasker’s own Stripe schedule and can’t be seen from here.
              </p>

              <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-border-light"><h2 className="text-lg font-semibold text-white">Job-by-Job Ledger</h2></div>
                {!earnings.jobs || earnings.jobs.length === 0 ? (
                  <p className="text-center py-8 text-gray-400">No completed jobs yet</p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-light">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Job</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Customer</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Base Price</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Commission</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Insurance</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Service Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Processing Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Tax on Fees</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Earned (Net)</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Paid to Stripe</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earnings.jobs.map((j: any) => (
                        <tr key={j.taskId} className="border-b border-border-light">
                          <td className="px-5 py-4 text-sm text-white">
                            {j.taskTitle || 'Untitled job'}
                            {j.cancelled && (
                              <span className="block text-xs text-yellow-400">
                                Cancelled by the customer — {j.partialPayment ? 'part paid' : 'paid in full (late cancellation)'}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-300">{j.customerName || '—'}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{j.finalPrice != null ? `$${j.finalPrice.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-red-400">{j.commissionAmount != null ? `-$${j.commissionAmount.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-red-400">{j.insuranceFeeTasker ? `-$${j.insuranceFeeTasker.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-red-400">{j.serviceFeeTasker ? `-$${j.serviceFeeTasker.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-red-400">{j.processingFeeTasker ? `-$${j.processingFeeTasker.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-red-400">{j.taxOnFees ? `-$${j.taxOnFees.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-green-400 font-medium">${(j.netReceived ?? 0).toFixed(2)}</td>
                          <td className="px-5 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${j.transferred ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                              {j.transferred ? 'Collected' : 'Not yet'}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-400">{formatDate(j.completedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'insurance' && (
        <div>
          <div className="bg-surface-card border border-border-light rounded-2xl p-5 mb-4">
            <p className="text-xs text-gray-400">
              Insurance policies submitted by this expert. A verified policy that clears the job window plus a 30-day buffer
              waives the matching CategoryInsuranceFee row for both customer and tasker on future hires.
              Re-uploading a policy resets it to unverified — admin must re-verify before the waiver applies again.
            </p>
          </div>

          {!insurancePoliciesLoaded ? (
            <p className="text-center py-8 text-gray-400">Loading insurance policies…</p>
          ) : insurancePolicies.length === 0 ? (
            <p className="text-center py-8 text-gray-400 bg-surface-card border border-border-light rounded-2xl">
              No insurance policies on file. This expert has not submitted any coverage for waiver eligibility.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {insurancePolicies.map((policy) => (
                <div key={policy.id} className="bg-surface-card border border-border-light rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {INSURANCE_TYPE_LABELS[policy.insuranceType] || policy.insuranceType}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{policy.provider}</p>
                    </div>
                    <span className={`px-2.5 py-1 text-xs rounded-full ${
                      policy.isVerified ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'
                    }`}>
                      {policy.isVerified ? '✓ Verified' : 'Pending Verification'}
                    </span>
                  </div>

                  {policy.policyNumber && (
                    <p className="text-xs text-gray-400 mb-1">Policy #: {policy.policyNumber}</p>
                  )}
                  <p className="text-xs text-gray-400 mb-1">
                    Expires: {policy.expiresAt ? new Date(policy.expiresAt).toLocaleDateString() : 'No expiry set'}
                  </p>
                  {policy.verifiedAt && (
                    <p className="text-xs text-gray-500 mb-1">
                      Verified on {new Date(policy.verifiedAt).toLocaleDateString()}
                    </p>
                  )}
                  {policy.note && (
                    <p className="text-xs text-gray-400 mb-1 italic">Note: {policy.note}</p>
                  )}

                  {/* FIX: this card previously answered "is this document
                      verified" but never "does this policy actually waive
                      anything, and for what." Admin had no way to tell
                      "fine" from "expired for practical purposes" without
                      manually redoing the same 30-day-buffer math
                      resolveInsuranceFees does at hire time, per category,
                      by hand. coverageByCategory (admin.service.js's
                      listExpertInsurancePolicies) answers both: which
                      categories this insuranceType matters for, and the
                      latest job date this policy would still cover for
                      each — using the EXACT SAME constant and date math
                      the real hire-time check uses, so this can never
                      quietly disagree with what actually gets charged. */}
                  {policy.isVerified && (
                    <div className="mb-2 pt-2 border-t border-border-light">
                      {!policy.coverageByCategory || policy.coverageByCategory.length === 0 ? (
                        <p className="text-xs text-gray-500">
                          Not currently applicable to any category this expert offers — no category with a{' '}
                          {INSURANCE_TYPE_LABELS[policy.insuranceType] || policy.insuranceType} fee row set to waive on verification.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500 font-medium">Waiver applies to:</p>
                          {policy.coverageByCategory.map((cov) => {
                            const isFuturePractical = !cov.latestCoverableScheduleDate
                              || new Date(cov.latestCoverableScheduleDate) >= new Date()
                            return (
                              <div key={cov.categoryId} className="flex items-center justify-between text-xs">
                                <span className="text-gray-300">{cov.categoryName}</span>
                                <span className={isFuturePractical ? 'text-green-400' : 'text-red-400'}>
                                  {cov.latestCoverableScheduleDate
                                    ? `Covers jobs scheduled through ${new Date(cov.latestCoverableScheduleDate).toLocaleDateString()}`
                                    : 'Covers any schedule (no expiry)'}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mb-3">
                    Submitted {new Date(policy.createdAt).toLocaleDateString()}
                  </p>

                  {/* New: the certificate backing this policy, now that
                      admin.service.js's listExpertInsurancePolicies joins
                      it in via documentId. Was previously nowhere to be
                      seen from this tab at all — the only way to check
                      the actual document was to go hunting in the
                      separate Documents tab, if it was even still there.
                      (policy as any) matches the existing convention in
                      this file for fields not yet added to the shared
                      ExpertInsurancePolicy type in ../types — add
                      documentFileUrl / documentFileName there when
                      convenient.) */}
                  {/* FIX: this was an <a href> straight to documentFileUrl —
                      a signed link from the LIST response, so it skipped
                      the MFA check and audit log every other document
                      view goes through, and it stopped working 5 minutes
                      after the tab loaded (the link's lifetime). It now
                      opens the same audited preview as the Documents tab. */}
                  {policy.documentId && (
                    <button
                      type="button"
                      onClick={() => openDocumentPreview(
                        rawDocuments.find((d) => d.id === policy.documentId) ?? ({
                          id:         policy.documentId!,
                          expertId:   policy.expertId,
                          type:       policy.documentType || 'insurance_proof',
                          fileUrl:    '',
                          fileName:   policy.documentFileName || 'Insurance document',
                          isVerified: policy.isVerified,
                          uploadedAt: policy.documentUploadedAt || policy.createdAt,
                        } as AdminExpertDocument)
                      )}
                      className="inline-block text-xs text-brand-400 hover:text-brand-300 mb-3 text-left"
                    >
                      📄 View Document{policy.documentFileName ? ` (${policy.documentFileName})` : ''} →
                    </button>
                  )}

                  <div className="flex flex-col gap-2">
                    {/* Verify / Reject */}
                    {!policy.isVerified ? (
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            setActingPolicyId(policy.id)
                            try {
                              await verifyExpertInsurancePolicy(policy.id, { verified: true })
                              const res = await listExpertInsurancePolicies(id!)
                              setInsurancePolicies(res.data.data)
                            } catch (err: any) {
                              alert(err.response?.data?.message || 'Failed to verify policy')
                            } finally {
                              setActingPolicyId(null)
                            }
                          }}
                          disabled={actingPolicyId === policy.id}
                          className="flex-1 px-3 py-2 text-sm bg-green-500/10 text-green-400 rounded-xl hover:bg-green-500/20 disabled:opacity-50 transition-colors"
                        >
                          {actingPolicyId === policy.id ? 'Saving…' : 'Verify'}
                        </button>
                        <button
                          onClick={() => { setPolicyNoteModal(policy); setPolicyNoteText(policy.note || '') }}
                          disabled={actingPolicyId === policy.id}
                          className="flex-1 px-3 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setPolicyNoteModal(policy); setPolicyNoteText(policy.note || '') }}
                        disabled={actingPolicyId === policy.id}
                        className="w-full px-3 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                      >
                        Reject (undo verification)
                      </button>
                    )}
                    {/* Update expiry */}
                    <button
                      onClick={() => {
                        setPolicyExpiryModal(policy)
                        setPolicyExpiryText(policy.expiresAt ? policy.expiresAt.slice(0, 10) : '')
                      }}
                      className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-brand-400 transition-colors"
                    >
                      Update expiry date
                    </button>
                    {/* Delete */}
                    <button
                      onClick={() => setPolicyDeleteModal(policy)}
                      disabled={actingPolicyId === policy.id}
                      className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-red-400 disabled:opacity-50 transition-colors"
                    >
                      Delete policy
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Delete policy modal */}
          {policyDeleteModal && (
            createPortal(
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPolicyDeleteModal(null)}>
              <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-white mb-2">Delete this policy?</h3>
                <p className="text-sm text-gray-400 mb-4">
                  {policyDeleteModal.isVerified
                    ? 'This policy is currently verified. Deleting it removes the fee waiver it gives. '
                    : ''}
                  The record is removed for good. The uploaded certificate file is not deleted. If a past job used this policy it cannot be deleted. Reject it instead.
                </p>
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setPolicyDeleteModal(null)}
                    className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                  <button
                    onClick={async () => {
                      if (!policyDeleteModal) return
                      setActingPolicyId(policyDeleteModal.id)
                      try {
                        await deleteExpertInsurancePolicy(policyDeleteModal.id)
                        const res = await listExpertInsurancePolicies(id!)
                        setInsurancePolicies(res.data.data)
                        setPolicyDeleteModal(null)
                      } catch (err: any) {
                        alert(err.response?.data?.message || 'Failed to delete policy')
                      } finally {
                        setActingPolicyId(null)
                      }
                    }}
                    disabled={actingPolicyId === policyDeleteModal.id}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors"
                  >
                    {actingPolicyId === policyDeleteModal.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>, document.body)
          )}

          {/* Reject / note modal */}
          {policyNoteModal && (
            createPortal(
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPolicyNoteModal(null)}>
              <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {policyNoteModal.isVerified ? 'Reject Previously Verified Policy' : 'Reject Policy'}
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  {policyNoteModal.isVerified
                    ? 'Rejecting this reverses verification — any future hires will not benefit from this waiver until a new policy is submitted and verified.'
                    : 'The expert will need to submit a new policy. Add a note explaining what needs to change (optional but recommended).'}
                </p>
                <textarea
                  value={policyNoteText}
                  onChange={(e) => setPolicyNoteText(e.target.value)}
                  placeholder="e.g. Policy expired — please submit renewed certificate"
                  rows={3} autoFocus
                  className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4 resize-none"
                />
                <div className="flex gap-3 justify-end">
                  <button onClick={() => { setPolicyNoteModal(null); setPolicyNoteText('') }}
                    className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                  <button
                    onClick={async () => {
                      if (!policyNoteModal) return
                      setActingPolicyId(policyNoteModal.id)
                      try {
                        await verifyExpertInsurancePolicy(policyNoteModal.id, {
                          verified: false,
                          note: policyNoteText.trim() || undefined,
                        })
                        const res = await listExpertInsurancePolicies(id!)
                        setInsurancePolicies(res.data.data)
                        setPolicyNoteModal(null)
                        setPolicyNoteText('')
                      } catch (err: any) {
                        alert(err.response?.data?.message || 'Failed to reject policy')
                      } finally {
                        setActingPolicyId(null)
                      }
                    }}
                    disabled={actingPolicyId === policyNoteModal.id}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors"
                  >
                    {actingPolicyId === policyNoteModal.id ? 'Saving…' : 'Confirm Reject'}
                  </button>
                </div>
              </div>
            </div>, document.body)
          )}

          {/* Update expiry modal */}
          {policyExpiryModal && (
            createPortal(
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setPolicyExpiryModal(null)}>
              <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-semibold text-white mb-2">Update Policy Expiry</h3>
                <p className="text-sm text-gray-400 mb-4">
                  Use when the expert renewed and only the end date changed — the policy number and coverage are the same.
                  Leave blank to mark as having no expiry.
                </p>
                <label className="block text-xs text-gray-500 mb-1">Expiry Date</label>
                <input type="date" value={policyExpiryText} onChange={(e) => setPolicyExpiryText(e.target.value)}
                  className="w-full h-11 leading-normal bg-surface-tertiary border border-border-light rounded-xl px-4 text-white focus:border-brand-500 focus:outline-none mb-4"
                  style={{ colorScheme: 'dark' }} />
                <div className="flex gap-3 justify-end">
                  <button onClick={() => { setPolicyExpiryModal(null); setPolicyExpiryText('') }}
                    className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
                  <button
                    onClick={async () => {
                      if (!policyExpiryModal) return
                      setActingPolicyId(policyExpiryModal.id)
                      try {
                        const expiresAt = policyExpiryText ? new Date(policyExpiryText).toISOString() : null
                        await updateExpertInsurancePolicyExpiry(policyExpiryModal.id, { expiresAt })
                        const res = await listExpertInsurancePolicies(id!)
                        setInsurancePolicies(res.data.data)
                        setPolicyExpiryModal(null)
                        setPolicyExpiryText('')
                      } catch (err: any) {
                        alert(err.response?.data?.message || 'Failed to update expiry')
                      } finally {
                        setActingPolicyId(null)
                      }
                    }}
                    disabled={actingPolicyId === policyExpiryModal.id}
                    className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors"
                  >
                    {actingPolicyId === policyExpiryModal.id ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>, document.body)
          )}
        </div>
      )}

      {tab === 'documents' && (
        <div>
          {(() => {
            const bgStatus = expert.backgroundCheckStatus || 'not_started'
            const bgDone = !!expert.backgroundCheckPassed
            const bgInProgress = !bgDone && (bgStatus === 'manual_review' || bgStatus === 'pending')
            const bgFailed = bgStatus === 'failed'

            const catsDone = catCount > 0 && catCertifiedCount === catCount
            const catsPartial = catCertifiedCount > 0 && catCertifiedCount < catCount

            const docsDone = documents.length > 0

            type StepState = 'done' | 'current' | 'issue' | 'pending'
            const steps: { label: string; state: StepState; detail: string }[] = [
              {
                label: 'Background Check',
                state: bgFailed ? 'issue' : bgDone ? 'done' : bgInProgress ? 'current' : 'pending',
                detail: bgDone ? 'Passed' : bgFailed ? 'Failed' : bgInProgress ? 'In review' : 'Not started',
              },
              {
                label: 'Documents Uploaded',
                state: docsDone ? 'done' : 'pending',
                detail: docsDone ? `${documents.length} uploaded` : 'None yet',
              },
              {
                label: 'Categories Certified',
                state: catsDone ? 'done' : catsPartial ? 'current' : 'pending',
                detail: catCount === 0 ? 'No categories' : `${catCertifiedCount} of ${catCount}`,
              },
              {
                label: 'Approved',
                state: expert.status === 'approved' ? 'done' : 'pending',
                detail: expert.status === 'approved' ? 'Live on platform' : expert.status.replace(/_/g, ' '),
              },
            ]

            const stepIcon = (state: StepState) => {
              if (state === 'done')    return <CheckCircle2 size={20} className="text-green-400" />
              if (state === 'issue')   return <AlertCircle size={20} className="text-red-400" />
              if (state === 'current') return <Clock size={20} className="text-yellow-400" />
              return <Circle size={20} className="text-gray-600" />
            }

            return (
              <div className="bg-surface-card border border-border-light rounded-2xl p-5 mb-6">
                <div className="flex items-center">
                  {steps.map((step, i) => (
                    <React.Fragment key={step.label}>
                      <div className="flex flex-col items-center text-center w-32">
                        {stepIcon(step.state)}
                        <p className={`text-xs font-medium mt-2 ${step.state === 'done' ? 'text-white' : step.state === 'issue' ? 'text-red-400' : 'text-gray-400'}`}>
                          {step.label}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 capitalize">{step.detail}</p>
                      </div>
                      {i < steps.length - 1 && (
                        <div className={`flex-1 h-0.5 -mt-6 ${step.state === 'done' ? 'bg-green-500/40' : 'bg-border-light'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )
          })()}

          {(() => {
            const { pending, rejected, total: pendingCount } = tallyReviewStatus(documents)
            const allVerified  = documents.length > 0 && pendingCount === 0

            if (allVerified && expert.status !== 'approved') {
              return (
                <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={20} className="text-green-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-white">All documents verified</p>
                      <p className="text-xs text-gray-400">This expert is ready to be approved.</p>
                    </div>
                  </div>
                  {canModify && (
                    <button onClick={handleApproveExpert} disabled={approvingExpert}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-500 disabled:opacity-50 transition-colors shrink-0">
                      {approvingExpert ? 'Approving...' : 'Approve Expert'}
                    </button>
                  )}
                </div>
              )
            }

            // Previously this state was invisible for already-
            // approved experts — a newly added category's document
            // sat unreviewed with nothing on screen calling it out.
            if (pendingCount > 0 && expert.status === 'approved') {
              return (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
                  <AlertCircle size={20} className="text-yellow-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-white">
                      {reviewSummaryLabel({ pending, rejected })} — {pendingCount} document{pendingCount === 1 ? '' : 's'} not yet verified
                    </p>
                    <p className="text-xs text-gray-400">
                      This expert is already approved, but has {pendingCount === 1 ? 'a newer document' : 'newer documents'} — likely from adding a category — that {pendingCount === 1 ? "isn't" : "aren't"} yet verified.
                      {rejected > 0 && ' Rejected ones are waiting on the expert to re-upload, not on you.'}
                    </p>
                  </div>
                </div>
              )
            }

            if (expert.status === 'approved') {
              return (
                <div className="bg-surface-tertiary rounded-2xl p-3 mb-4 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-green-400" />
                  <p className="text-sm text-green-400">This expert is already approved, all documents verified.</p>
                </div>
              )
            }

            return null
          })()}

          {/* Approved, but the tasker has not finished payout (Stripe) / GST/HST yet. */}
          {expert.status === 'approved' && (onboarding as any)?.needsSetup === true && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4 mb-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white">Approved — finishing setup</p>
                <p className="text-xs text-gray-400">
                  The tasker still has to complete payout (verified by Stripe) and/or declare GST/HST. Until then the app
                  keeps them on the setup screen, and they cannot apply for or be hired on paid jobs.
                </p>
              </div>
            </div>
          )}

          {/* ── Onboarding checklist (the resolver's answer) ────────
              The dashboard previously had NO view of what an expert
              still owed. The only such computation on the admin side
              was approveCompany's local missing[] array, which existed
              purely to throw a 400 at whoever clicked Approve — three
              document types, no vehicles, no licence requirements, and
              nothing the admin could see before clicking.

              This reads GET /api/admin/experts/:id/onboarding-status:
              the same steps[], the same item statuses, the same
              rejection reasons the expert is looking at in their own
              app. Two people, one list. */}
          {onboarding && (
            <div className="bg-surface-card border border-border-light rounded-2xl p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white">Onboarding progress</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${onboarding.canSubmit ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                  {onboarding.canSubmit ? 'Ready to submit' : 'Incomplete'}
                </span>
              </div>

              {/* Everything the EXPERT must act on, with the reason we
                  gave them. If this list is non-empty, the ball is in
                  their court — approving around it is how a rejected
                  document ends up on an approved expert. */}
              {onboarding.actionRequired.length > 0 && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 mb-3">
                  <p className="text-xs font-semibold text-red-400 mb-2">
                    Waiting on the expert ({onboarding.actionRequired.length})
                  </p>
                  {onboarding.actionRequired.map((a) => (
                    <div key={`${a.stepKey}:${a.itemKey}`} className="mb-1.5 last:mb-0">
                      <p className="text-xs text-white">{a.label}</p>
                      {a.reason && <p className="text-xs text-gray-400">{a.reason}</p>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {onboarding.steps.map((step) => {
                  // Payout and GST/HST are "Finish setup": the tasker does them AFTER approval,
                  // so before approval they are not outstanding work for this review.
                  const isSetupStep = ((onboarding as any).setupStepKeys || []).includes(step.key)
                  if (isSetupStep && expert.status !== 'approved') {
                    return (
                      <span key={step.key} title="Done by the tasker after approval (Finish setup)"
                        className="px-2.5 py-1 text-xs rounded-full bg-gray-600/20 text-gray-400">
                        {step.label} · after approval
                      </span>
                    )
                  }
                  const outstanding = step.items.filter(
                    (i) => i.required && !['verified', 'submitted'].includes(i.status)
                  ).length
                  return (
                    <span
                      key={step.key}
                      title={outstanding > 0 ? `${outstanding} item(s) outstanding` : step.description}
                      className={`px-2.5 py-1 text-xs rounded-full ${STEP_STATUS_CLASS[step.status] || 'bg-gray-600/20 text-gray-400'}`}
                    >
                      {step.label}
                      {outstanding > 0 ? ` · ${outstanding}` : ''}
                    </span>
                  )
                })}
              </div>
            </div>
          )}

          {/* Company documents (registration, liability, commercial
              auto) are never shown on this page — see
              excludeCompanyDocs at the top of this file. Point
              admins there directly instead of leaving them to
              wonder where those three went. */}
          {isCompany && (
            <button
              onClick={() => navigate('/companies')}
              className="w-full flex items-center justify-between gap-3 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl p-4 mb-4 text-left hover:bg-indigo-500/20 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Building2 size={18} className="text-indigo-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white">This is a company account</p>
                  <p className="text-xs text-gray-400">Business registration, liability insurance, and commercial auto insurance are reviewed on the Companies tab, not here.</p>
                </div>
              </div>
              <span className="text-xs text-indigo-400 shrink-0">Go to Companies →</span>
            </button>
          )}

          {/* ── Documents sub-navigation ─────────────────────────
              Three genuinely separate review concerns, previously
              one long scrolling page: identity (government ID +
              selfie), the background check (its own status machine,
              not just another document), and per-category licence/
              certificate documents (+ nested vehicle documents).
              Counts badge whichever sub-tab has something pending
              or rejected, so an admin can see where to look without
              clicking through all three. */}
          {(() => {
            const identityCount = documents.filter((d) => IDENTITY_DOC_TYPES.has(d.type) && docStatusOf(d) !== 'verified').length
            const backgroundCount = documents.filter((d) => BACKGROUND_CHECK_DOC_TYPES.has(d.type) && docStatusOf(d) !== 'verified').length
            const categoryCount = documents.filter((d) => !isPersonalDocType(d.type) && docStatusOf(d) !== 'verified').length

            const subTabs: { key: typeof docSubTab; label: string; count: number }[] = [
              { key: 'identity', label: '🪪 Identity', count: identityCount },
              { key: 'background', label: '🛡️ Background Check', count: backgroundCount },
              { key: 'categories', label: '📋 Categories', count: categoryCount },
            ]

            return (
              <div className="flex gap-2 mb-4 border-b border-border-light">
                {subTabs.map((st) => (
                  <button
                    key={st.key}
                    onClick={() => setDocSubTab(st.key)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                      docSubTab === st.key
                        ? 'border-brand-500 text-white'
                        : 'border-transparent text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {st.label}
                    {st.count > 0 && (
                      <span className="px-1.5 py-0.5 text-[11px] rounded-full bg-yellow-600/20 text-yellow-400">{st.count}</span>
                    )}
                  </button>
                ))}
              </div>
            )
          })()}

          {/* Background check status banner — only relevant on that
              sub-tab, and only when there's no uploaded document to
              show its own status instead (see backgroundCheckDocs
              rendering further down, which covers the uploaded case). */}
          {docSubTab === 'background' && (() => {
            const bgDoc = documents.find((d) => d.type === 'external_background_check')
            const bgStatus = expert.backgroundCheckStatus || 'not_started'
            const viaUpload = !!bgDoc
            const viaPlatform = !viaUpload && bgStatus !== 'not_started'

            if (viaUpload) return null

            return (
              <div className="bg-surface-card border border-border-light rounded-2xl p-4 mb-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-white flex items-center gap-2"><ShieldCheck size={16} className="text-gray-400" /> Background Check</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    expert.backgroundCheckPassed ? 'bg-green-600/20 text-green-400' :
                    bgStatus === 'manual_review' ? 'bg-yellow-600/20 text-yellow-400' :
                    bgStatus === 'failed' ? 'bg-red-600/20 text-red-400' : 'bg-gray-600/20 text-gray-400'
                  }`}>
                    {expert.backgroundCheckPassed ? 'Passed' : bgStatus.replace(/_/g, ' ')}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {viaPlatform
                    ? 'Completed via: platform (Certn) — no document to review, tracked automatically'
                    : 'Not started yet — expert has not chosen either background check option'}
                </p>
              </div>
            )
          })()}

          {(() => {
            if (documents.length === 0) {
              return <p className="text-center py-8 text-gray-400 bg-surface-card border border-border-light rounded-2xl">No documents uploaded yet</p>
            }

            // Prefer the specific requirement label this document
            // actually proves (e.g. "IICRC WRT Certification",
            // "Host/Employing Agency — Active TICO Registration")
            // over the generic doc.type — several categories share
            // the same type (e.g. every 'license' upload) but mean
            // completely different things depending on which
            // requirement it's for. Vehicle documents don't have a
            // licenseRequirement (they're not category-scoped), so
            // for those, prefix with which vehicle it's for instead.
            const getDocLabel = (doc: AdminExpertDocument) => {
              if (doc.licenseRequirement?.label) return doc.licenseRequirement.label
              if (isPersonalDocType(doc.type)) return PERSONAL_DOC_LABELS[doc.type]
              const readableType = doc.type.replace(/_/g, ' ')
              if (doc.vehicleType) {
                const vehicleLabel = doc.vehicleType.charAt(0).toUpperCase() + doc.vehicleType.slice(1)
                return `${vehicleLabel} — ${readableType}`
              }
              return readableType
            }

            const renderDoc = (doc: AdminExpertDocument) => {
              const status = docStatusOf(doc)
              return (
                <div key={doc.id} className="bg-surface-card border border-border-light rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-white capitalize flex items-center gap-2">
                      <FileText size={14} className="text-gray-500" />
                      {getDocLabel(doc)}
                      {/* Automatic: comes from the requirement's own Optional setting. */}
                      {/* 2026-10-09: "Optional" applies to the INDIVIDUAL path only. A
                          company, or someone who chose Company at signup, must
                          provide every line — insurance included — so it is
                          shown as required for them. */}
                      {doc.licenseRequirement?.isOptional === true && (
                        ((expert as any)?.expertType === 'company' || (expert as any)?.pendingCompanyConversion === true) ? (
                          <span className="px-2 py-0.5 text-[11px] normal-case rounded-full bg-indigo-600/20 text-indigo-300"
                            title="Optional for individuals, but this tasker is on the company path — companies must always provide it.">
                            Required (company)
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-[11px] normal-case rounded-full bg-gray-500/20 text-gray-300"
                            title="Optional for individuals — only removes the insurance fee.">
                            Optional
                          </span>
                        )
                      )}
                      {/* 2026-10-10: an incorporated company / partnership is a separate
                          legal entity — its insurance must name the BUSINESS. A policy
                          verified here counts for the company (Company Profile), so check
                          the named insured before approving. */}
                      {!!doc.licenseRequirement?.insuranceType
                        && ['incorporated', 'partnership'].includes((expert as any)?.businessType)
                        && ((expert as any)?.expertType === 'company' || (expert as any)?.pendingCompanyConversion === true) && (
                        <span className="px-2 py-0.5 text-[11px] normal-case rounded-full bg-amber-600/20 text-amber-300"
                          title="This tasker is an incorporated company or partnership. Approve only if the policy names the business as the insured, not only the owner.">
                          Check insured: {(expert as any)?.businessName || 'the business'}
                        </span>
                      )}
                    </p>
                    <span className={`px-2 py-1 text-xs rounded-full ${DOC_STATUS_CLASS[status]}`}>
                      {DOC_STATUS_LABEL[status]}
                    </span>
                  </div>
                  {doc.fileName && (
                    <p className="text-xs text-gray-400 mb-2 truncate" title={doc.fileName}>{doc.fileName}</p>
                  )}
                  <p className="text-xs text-gray-500 mb-3">Uploaded {new Date(doc.uploadedAt).toLocaleString()}</p>
                  {(doc as any).expertDeclaredExpiresAt && (
                    <p className="text-xs text-gray-400 -mt-2 mb-3">
                      Expires (entered by tasker): {new Date((doc as any).expertDeclaredExpiresAt).toLocaleDateString()}
                    </p>
                  )}
                  {(doc as any).expiryConfirmedAt && (
                    <p className="text-xs text-gray-400 -mt-2 mb-3">
                      Expires (confirmed by admin):{' '}
                      <span className="text-white">
                        {(doc as any).adminConfirmedExpiresAt ? new Date((doc as any).adminConfirmedExpiresAt).toLocaleDateString() : 'No expiry'}
                      </span>
                    </p>
                  )}
                  {(doc as any).adminConfirmedInsurer && (
                    <p className="text-xs text-gray-400 -mt-2 mb-3">
                      Insurance company: <span className="text-white">{(doc as any).adminConfirmedInsurer}</span>
                    </p>
                  )}
                  <button onClick={() => openDocumentPreview(doc)}
                    className="mb-3 text-sm text-brand-400 hover:text-brand-300">
                    Preview File →
                  </button>
                  {doc.rejectionReason && !doc.isVerified && (
                    <p className="text-xs text-red-400 mb-3">
                      Rejected: {doc.rejectionReason}
                      {doc.reviewedAt ? ` — ${new Date(doc.reviewedAt).toLocaleDateString()}` : ''}
                    </p>
                  )}
                  {/* FIX: this div had no `!doc.isVerified` guard at all —
                      a fully verified, approved document still showed
                      "Request Reupload"/"Approve" underneath it, identical
                      to a never-reviewed one. Also relabels once a
                      rejection is already on record, same reasoning as
                      renderIdentityRow above. */}
                  {!doc.isVerified ? (
                    <div className="flex gap-2">
                      <button onClick={() => { setReuploadModal(doc); setReuploadReason(doc.rejectionReason || '') }} disabled={actingDocId === doc.id}
                        className="flex-1 px-3 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                        {status === 'action_required' ? 'Update Rejection' : 'Request Reupload'}
                      </button>
                      <button
                        onClick={() => needsDateConfirm(doc) ? openInsuranceVerifyModal(doc) : handleVerifyDocument(doc.id, true)}
                        disabled={actingDocId === doc.id}
                        className="flex-1 px-3 py-2 text-sm bg-green-500/10 text-green-400 rounded-xl hover:bg-green-500/20 disabled:opacity-50 transition-colors">
                        {actingDocId === doc.id ? 'Saving...' : 'Approve'}
                      </button>
                    </div>
                  ) : (
                    // A verification made in error should be reversible —
                    // an admin who spots a problem later (a forged ID that
                    // slipped through, a licence that's since expired)
                    // needs a way back to action_required, not just a
                    // read-only "Verified" state forever. Uses the SAME
                    // reuploadModal/handleVerifyDocument(id, false, reason)
                    // path as a first-time rejection — no separate code
                    // path to keep in sync, and it correctly re-triggers
                    // verifyDocument's own demotion logic on the backend
                    // (see admin.service.js) if this document is one of
                    // the ones that gates the expert's own approval.
                    <button onClick={() => { setReuploadModal(doc); setReuploadReason('') }} disabled={actingDocId === doc.id}
                      className="w-full px-3 py-2 text-sm bg-red-500/10 text-red-400 rounded-xl hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                      Reject (undo verification)
                    </button>
                  )}
                  <button onClick={() => handleDeleteDocument(doc)} disabled={actingDocId === doc.id}
                    className="w-full mt-2 px-3 py-1.5 text-xs text-gray-500 hover:text-red-400 transition-colors">
                    Delete permanently
                  </button>
                </div>
              )
            }

            // Compact horizontal row for a single identity document
            // — a lighter-weight treatment than the full card, since
            // there's normally just one of each and they're personal
            // verification items, not category-specific requirements.
            const renderIdentityRow = (doc: AdminExpertDocument) => {
              const status = docStatusOf(doc)
              return (
                <div key={doc.id} className="bg-surface-card border border-border-light rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={14} className="text-gray-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{getDocLabel(doc)}</p>
                        <button onClick={() => openDocumentPreview(doc)} className="text-xs text-brand-400 hover:text-brand-300">
                          Preview File →
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`px-2 py-1 text-xs rounded-full ${DOC_STATUS_CLASS[status]}`}>
                        {DOC_STATUS_LABEL[status]}
                      </span>
                      {!doc.isVerified ? (
                        <>
                          {/* FIX: this used to always say "Reject" regardless
                              of whether one was already on file — identical
                              to a never-reviewed document, which is exactly
                              why a rejection looked like it hadn't happened.
                              Relabeled once status is action_required so the
                              button itself confirms a rejection is already
                              recorded, not just the badge above it. */}
                          <button onClick={() => { setReuploadModal(doc); setReuploadReason(doc.rejectionReason || '') }} disabled={actingDocId === doc.id}
                            className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                            {status === 'action_required' ? 'Update Rejection' : 'Reject'}
                          </button>
                          <button
                            onClick={() => isInsuranceLinkedDoc(doc) ? openInsuranceVerifyModal(doc) : handleVerifyDocument(doc.id, true)}
                            disabled={actingDocId === doc.id}
                            className="px-3 py-1.5 text-xs bg-green-500/10 text-green-400 rounded-lg hover:bg-green-500/20 disabled:opacity-50 transition-colors">
                            {actingDocId === doc.id ? '...' : 'Approve'}
                          </button>
                        </>
                      ) : (
                        // Same undo-a-verification path as renderDoc above —
                        // a verified document isn't a dead end; an admin who
                        // notices a problem later can still walk it back.
                        <button onClick={() => { setReuploadModal(doc); setReuploadReason('') }} disabled={actingDocId === doc.id}
                          className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 disabled:opacity-50 transition-colors">
                          Reject
                        </button>
                      )}
                    </div>
                  </div>
                  {/* FIX: the reason typed into the reject modal was never
                      shown anywhere on this row — renderDoc (the category-
                      scoped card layout) already displayed it; this compact
                      identity-document layout simply never gained the same
                      line, so a rejected government ID looked identical to
                      one nobody had reviewed yet, and the admin who
                      rejected it (or the next one reviewing the queue) had
                      no way to see what reason was actually given. */}
                  {doc.rejectionReason && !doc.isVerified && (
                    <p className="text-xs text-red-400 mt-2 pl-6">
                      Rejected: {doc.rejectionReason}
                      {doc.reviewedAt ? ` — ${new Date(doc.reviewedAt).toLocaleDateString()}` : ''}
                    </p>
                  )}
                </div>
              )
            }

            const toggleSection = (key: string) => {
              setExpandedDocSections((prev) => {
                const next = new Set(prev)
                if (next.has(key)) next.delete(key)
                else next.add(key)
                return next
              })
            }

            // Identity documents (selfie, government ID) pulled out
            // entirely — personal verification items, not tied to
            // any specific category or vehicle. Background check
            // proof is pulled out separately from these (see
            // BACKGROUND_CHECK_DOC_TYPES above) so it can render as
            // its own sub-tab instead of being lumped in with ID
            // photos — it has its own status machine (Certn vs.
            // uploaded proof) that reads confusingly next to a
            // simple verified/rejected document.
            const identityDocs = documents.filter((d) => IDENTITY_DOC_TYPES.has(d.type))
            const backgroundCheckDocs = documents.filter((d) => BACKGROUND_CHECK_DOC_TYPES.has(d.type))

            // Grouping by category is the actual fix for the
            // visibility gap discussed earlier: previously a
            // second/replacement document for an already-certified
            // category looked identical to any other row in one
            // long flat list. Grouped, a category with a pending
            // document is immediately distinguishable from one
            // that's fully settled.
            //
            // Vehicle documents don't have a category at all
            // (they're scoped by vehicleType instead) — previously
            // these fell straight into the flat "Other Documents"
            // bucket with zero indication of which vehicle they
            // belonged to. Grouped by vehicle type now, same as
            // category grouping — a "🚗 Car" section is exactly as
            // scannable as a category section, instead of being
            // buried anonymously among selfies and ID photos.
            const byCategory = new Map<string, { docs: AdminExpertDocument[], categoryId: string }>()
            const byVehicle  = new Map<string, AdminExpertDocument[]>()
            // NEW: liability_insurance documents pulled out into their
            // own bucket, same reason vehicle documents get one — they
            // carry no categoryId at all (see CategoriesScreen.tsx's
            // uploadLiabilityInsuranceDocument, which deliberately omits
            // it: one document satisfies every category that requires
            // it, so it can't be scoped to just one). Without this they
            // fell into `general` ("Other Documents") indistinguishably
            // from an unrelated document, with no link back to the
            // category whose requiresLiabilityInsurance checkbox is what
            // actually asked the expert to upload it.
            const liabilityInsuranceDocs: AdminExpertDocument[] = []
            const general: AdminExpertDocument[] = []
            // FIX: grouping used to trust doc.category (the populated
            // relation object) alone. When that relation doesn't resolve
            // for any reason — the comment below already documented this
            // happening for licenseRequirement-scoped docs — the document
            // silently fell into "Other Documents" instead of its real
            // category, undercounting that category's total (a category
            // could show "0 documents" while the expert app's resolver,
            // which matches on the raw categoryId column directly and
            // doesn't depend on this relation at all, correctly found and
            // surfaced the same document). This map lets any document
            // with a categoryId fall back to its real category by id even
            // when the relation object is missing, so the two pages can't
            // disagree about which category a document belongs to.
            const categoryNameById = new Map<string, string>(
              (expert.categories || []).map((ec: any) => [ec.category.id, ec.category.name])
            )
            for (const doc of documents) {
              if (isPersonalDocType(doc.type)) continue
              const categoryName = doc.category?.name ?? (doc.categoryId ? categoryNameById.get(doc.categoryId) : undefined)
              const categoryId   = doc.category?.id ?? doc.categoryId ?? undefined
              if (categoryName && categoryId) {
                if (!byCategory.has(categoryName)) byCategory.set(categoryName, { docs: [], categoryId })
                byCategory.get(categoryName)!.docs.push(doc)
              } else if (doc.vehicleType) {
                const key = doc.vehicleType.charAt(0).toUpperCase() + doc.vehicleType.slice(1)
                if (!byVehicle.has(key)) byVehicle.set(key, [])
                byVehicle.get(key)!.push(doc)
              } else if (doc.type === 'liability_insurance') {
                liabilityInsuranceDocs.push(doc)
              } else {
                general.push(doc)
              }
            }

            // Also add categories that have licenseRequirement-scoped
            // docs (doc.category is null on those) but ARE selected by
            // the expert — pulling from expert.categories directly.
            for (const ec of (expert.categories || [])) {
              const name = ec.category.name
              if (!byCategory.has(name)) {
                byCategory.set(name, { docs: [], categoryId: ec.category.id })
              }
            }

            // Which of the expert's SELECTED categories actually
            // require a vehicle (same group list the expert app
            // uses to decide whether to show its own vehicle
            // section) — a vehicle isn't strictly tied to one
            // category in the data model, but in practice an
            // expert uploads vehicle documents because of a
            // specific category they picked, so nesting under
            // whichever selected category actually needs a
            // vehicle is the right call over showing it as an
            // unrelated separate block.
            const VEHICLE_CATEGORY_GROUPS = new Set(['Moving & Hauling', 'Delivery', 'Food Delivery & Meal Services', 'Shopping & Errands'])
            const vehicleRequiringCategoryNames = new Set(
              (expert.categories || [])
                .filter((ec) => VEHICLE_CATEGORY_GROUPS.has(ec.category.group || ''))
                .map((ec) => ec.category.name)
            )
            const hasVehicleRequiringCategory = vehicleRequiringCategoryNames.size > 0

            // NEW: which of the expert's SELECTED categories have the
            // admin's "Requires liability insurance" checkbox on
            // (Category.requiresLiabilityInsurance — schema.prisma).
            // Same nesting rule as vehicles above: the document itself
            // isn't tied to one category, but in practice the expert
            // uploaded it because of a specific requiring category, so
            // it's shown nested under every one that actually needs it
            // rather than as an unrelated standalone block.
            const liabilityInsuranceRequiringCategoryNames = new Set(
              (expert.categories || [])
                .filter((ec: any) => ec.category.requiresLiabilityInsurance)
                .map((ec: any) => ec.category.name)
            )
            const hasLiabilityInsuranceRequiringCategory = liabilityInsuranceRequiringCategoryNames.size > 0

            return (
              <div className="space-y-6">
                {docSubTab === 'identity' && (
                  identityDocs.length > 0 ? (
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-3">🪪 Identity Verification</h3>
                      <div className="space-y-2">{identityDocs.map(renderIdentityRow)}</div>
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-400 bg-surface-card border border-border-light rounded-2xl">No identity documents uploaded yet</p>
                  )
                )}

                {docSubTab === 'background' && backgroundCheckDocs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-3">🛡️ Background Check</h3>
                    <div className="space-y-2">{backgroundCheckDocs.map(renderIdentityRow)}</div>
                  </div>
                )}

                {docSubTab === 'categories' && (
                <>
                {Array.from(byCategory.entries()).map(([categoryName, entry]) => {
                  const docs = Array.isArray(entry) ? entry : entry.docs
                  const categoryId = Array.isArray(entry) ? expert.categories?.find((ec: any) => ec.category.name === categoryName)?.category.id : entry.categoryId
                  const docsTally = tallyReviewStatus(docs)
                  // Nest every vehicle document group under any
                  // category that requires one — usually there's
                  // exactly one such category, so this cleanly
                  // matches "this vehicle belongs to this
                  // category". If an expert has more than one
                  // vehicle-requiring category selected, the same
                  // vehicle intentionally appears under each — the
                  // data genuinely doesn't say which one it's
                  // "really" for, so showing it wherever it could
                  // be relevant beats hiding it or guessing wrong.
                  const nestedVehicleEntries = vehicleRequiringCategoryNames.has(categoryName)
                    ? Array.from(byVehicle.entries())
                    : []
                  const nestedTally  = tallyReviewStatus(nestedVehicleEntries.flatMap(([, vDocs]) => vDocs))
                  const nestedCount  = nestedVehicleEntries.reduce((sum, [, vDocs]) => sum + vDocs.length, 0)
                  // NEW: same nesting as vehicle documents above, but
                  // for the single, expert-level liability_insurance
                  // document — shown here whenever this category is
                  // one of the (possibly several) selected categories
                  // with requiresLiabilityInsurance set.
                  const nestedLiabilityDocs = liabilityInsuranceRequiringCategoryNames.has(categoryName)
                    ? liabilityInsuranceDocs
                    : []
                  const nestedLiabilityTally = tallyReviewStatus(nestedLiabilityDocs)
                  const totalTally   = {
                    pending:  docsTally.pending  + nestedTally.pending  + nestedLiabilityTally.pending,
                    rejected: docsTally.rejected + nestedTally.rejected + nestedLiabilityTally.rejected,
                  }
                  const totalCount   = docs.length + nestedCount + nestedLiabilityDocs.length
                  const key = `cat:${categoryName}`
                  const isOpen = expandedDocSections.has(key)
                  return (
                    <div key={categoryName}>
                      <div className="w-full flex items-center justify-between gap-2 mb-3">
                        <button onClick={() => toggleSection(key)} className="flex items-center gap-2 text-left flex-1">
                          <span className="text-gray-500 text-xs">{isOpen ? '▼' : '▶'}</span>
                          <h3 className="text-sm font-semibold text-white">{categoryName}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${totalTally.rejected > 0 ? 'bg-red-600/20 text-red-400' : totalTally.pending > 0 ? 'bg-yellow-600/20 text-yellow-400' : 'bg-green-600/20 text-green-400'}`}>
                            {reviewSummaryLabel(totalTally)}
                          </span>
                        </button>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-gray-500">{totalCount} document{totalCount === 1 ? '' : 's'}</span>
                          {categoryId && (
                            <button
                              onClick={() => handleRemoveCategory(categoryId, categoryName)}
                              className="px-2 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                              title="Remove this category and all its documents"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      {isOpen && (
                        <div className="space-y-4">
                          {docs.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{docs.map(renderDoc)}</div>}
                          {nestedVehicleEntries.map(([vehicleName, vDocs]) => (
                            <div key={`nested-vehicle-${vehicleName}`} className="pl-4 border-l-2 border-border-light">
                              <h4 className="text-xs font-semibold text-gray-400 mb-2">🚗 {vehicleName} — vehicle for this category</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{vDocs.map(renderDoc)}</div>
                            </div>
                          ))}
                          {nestedLiabilityDocs.length > 0 && (
                            <div className="pl-4 border-l-2 border-border-light">
                              <h4 className="text-xs font-semibold text-gray-400 mb-2">🛡️ Liability Insurance — required for this category</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{nestedLiabilityDocs.map(renderDoc)}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                {/* Only shown when no selected category actually
                    requires a vehicle — e.g. the category was
                    later removed but vehicle documents remain.
                    Keeps them visible instead of silently
                    disappearing, rather than a normal case. */}
                {/* FIX: this rendered a bare "🚗 Car" heading with
                    no indication it was a vehicle at all — identical
                    in appearance to a category name, which is exactly
                    the bug this file was reported with. Prefixed with
                    "Vehicle:" now, matching the same disambiguation
                    admin.service.js's groupLabel applies server-side. */}
                {!hasVehicleRequiringCategory && Array.from(byVehicle.entries()).map(([vehicleName, docs]) => {
                  const tally = tallyReviewStatus(docs)
                  const key = `veh:${vehicleName}`
                  const isOpen = expandedDocSections.has(key)
                  return (
                    <div key={`vehicle-${vehicleName}`}>
                      <div className="w-full flex items-center justify-between gap-2 mb-3">
                        <button onClick={() => toggleSection(key)} className="flex items-center gap-2 text-left flex-1">
                          <span className="text-gray-500 text-xs">{isOpen ? '▼' : '▶'}</span>
                          <h3 className="text-sm font-semibold text-white">🚗 Vehicle: {vehicleName}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${tally.rejected > 0 ? 'bg-red-600/20 text-red-400' : tally.pending > 0 ? 'bg-yellow-600/20 text-yellow-400' : 'bg-green-600/20 text-green-400'}`}>
                            {reviewSummaryLabel(tally)}
                          </span>
                        </button>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-gray-500">{docs.length} document{docs.length === 1 ? '' : 's'}</span>
                          <button
                            onClick={() => handleRemoveVehicle(vehicleName.toLowerCase())}
                            className="px-2 py-1 text-xs bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                            title="Remove this vehicle and all its documents"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      {isOpen && (
                        <>
                          <p className="text-xs text-gray-500 mb-2 pl-6">
                            No category currently requires this vehicle — it may belong to a category that was removed. These documents are orphaned; consider deleting them if the vehicle is no longer in use.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{docs.map(renderDoc)}</div>
                        </>
                      )}
                    </div>
                  )
                })}

                {/* FIX: mirrors the vehicle fallback block above —
                    if no currently-selected category requires
                    liability insurance anymore (requirement turned
                    off, or the category removed) but a document still
                    exists, show it here rather than silently losing
                    it in the flat "Other Documents" bucket with no
                    explanation of what it was for. */}
                {!hasLiabilityInsuranceRequiringCategory && liabilityInsuranceDocs.length > 0 && (() => {
                  const tally = tallyReviewStatus(liabilityInsuranceDocs)
                  const key = 'liability_insurance:orphan'
                  const isOpen = expandedDocSections.has(key)
                  return (
                    <div key="liability-insurance-orphan">
                      <div className="w-full flex items-center justify-between gap-2 mb-3">
                        <button onClick={() => toggleSection(key)} className="flex items-center gap-2 text-left flex-1">
                          <span className="text-gray-500 text-xs">{isOpen ? '▼' : '▶'}</span>
                          <h3 className="text-sm font-semibold text-white">🛡️ Liability Insurance</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${tally.rejected > 0 ? 'bg-red-600/20 text-red-400' : tally.pending > 0 ? 'bg-yellow-600/20 text-yellow-400' : 'bg-green-600/20 text-green-400'}`}>
                            {reviewSummaryLabel(tally)}
                          </span>
                        </button>
                        <span className="text-xs text-gray-500 shrink-0">{liabilityInsuranceDocs.length} document{liabilityInsuranceDocs.length === 1 ? '' : 's'}</span>
                      </div>
                      {isOpen && (
                        <>
                          <p className="text-xs text-gray-500 mb-2 pl-6">
                            No selected category currently requires liability insurance — it may belong to a category that was later removed or had this requirement turned off. These documents are orphaned; consider deleting them if no longer needed.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{liabilityInsuranceDocs.map(renderDoc)}</div>
                        </>
                      )}
                    </div>
                  )
                })()}

                {general.length > 0 && (
                  <div>
                    {(byCategory.size > 0 || byVehicle.size > 0 || liabilityInsuranceDocs.length > 0) && <h3 className="text-sm font-semibold text-gray-400 mb-3">Other Documents</h3>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{general.map(renderDoc)}</div>
                  </div>
                )}

                {byCategory.size === 0 && byVehicle.size === 0 && liabilityInsuranceDocs.length === 0 && general.length === 0 && (
                  <p className="text-center py-8 text-gray-400 bg-surface-card border border-border-light rounded-2xl">No category documents uploaded yet</p>
                )}
                </>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {showSubOverride && (
        createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowSubOverride(false)}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Override Subscription</h3>
            <p className="text-sm text-gray-400 mb-4">
              Manually sets the expert's subscription state — no charge occurs. The expert is notified of this change.
            </p>

            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select value={subOverrideStatus} onChange={(e) => setSubOverrideStatus(e.target.value as any)}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none mb-4">
              <option value="free">Free</option>
              <option value="active">Active</option>
              <option value="past_due">Past Due</option>
            </select>

            <label className="block text-xs text-gray-500 mb-1">Renews / Due Date</label>
            <input type="date" value={subOverrideRenewsAt} onChange={(e) => setSubOverrideRenewsAt(e.target.value)}
              className="w-full h-11 leading-normal bg-surface-tertiary border border-border-light rounded-xl px-4 text-white focus:border-brand-500 focus:outline-none mb-4"
              style={{ colorScheme: 'dark' }} />

            <label className="block text-xs text-gray-500 mb-1">Reason (shown to the expert)</label>
            <textarea value={subOverrideReason} onChange={(e) => setSubOverrideReason(e.target.value)}
              placeholder="e.g. Comped a free year as a goodwill gesture" rows={3}
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4 resize-none" />

            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowSubOverride(false)}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={submitSubOverride} disabled={savingSubOverride}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-xl hover:bg-brand-500 disabled:opacity-50 transition-colors">
                {savingSubOverride ? 'Saving...' : 'Save Override'}
              </button>
            </div>
          </div>
        </div>, document.body)
      )}

      {reuploadModal && (
        createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setReuploadModal(null)}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">
              {reuploadModal.isVerified
                ? 'Reject Previously Verified Document'
                : reuploadModal.rejectionReason ? 'Update Rejection Reason' : 'Request Reupload'}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {reuploadModal.isVerified
                ? 'This document was already verified. Rejecting it now undoes that — the expert will need to re-upload, and if this document is required for their own approval, rejecting it may move their account out of approved status.'
                : reuploadModal.rejectionReason
                  ? 'This document is already rejected and awaiting the expert. Editing and sending below replaces the reason they see.'
                  : 'The expert will be notified with this reason — be specific enough that they know exactly what to fix.'}
            </p>
            <textarea value={reuploadReason} onChange={(e) => setReuploadReason(e.target.value)}
              placeholder="e.g. Photo is blurry — please retake in better lighting" rows={3} autoFocus
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4 resize-none" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setReuploadModal(null); setReuploadReason('') }}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={submitReupload} disabled={!reuploadReason.trim() || actingDocId === reuploadModal.id}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors">
                {actingDocId === reuploadModal.id ? 'Sending...' : 'Send & Notify Expert'}
              </button>
            </div>
          </div>
        </div>, document.body)
      )}

      {/* Insurance-linked document approval — collects what a plain
          Approve click cannot: which InsuranceType this satisfies
          (only when the document type itself doesn't already say),
          the provider, and a real expiry date. Approving here both
          verifies the document AND creates/updates the matching
          ExpertInsurancePolicy row server-side — see
          admin.service.js's verifyDocument. */}
      {insuranceVerifyModal && (
        createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setInsuranceVerifyModal(null)}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">
              {isInsuranceLinkedDoc(insuranceVerifyModal) ? 'Verify Insurance Document' : 'Confirm Expiry Date'}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {isInsuranceLinkedDoc(insuranceVerifyModal)
                ? "This document proves an insurance policy — approving it also records the policy itself, which is what determines whether this type is waived on future hires. A real expiry date is required; that's exactly the gap that let a verified document sit with no expiry check ever applying to it."
                : 'Check the expiry date against the document and confirm it before approving. This is the date used for renewal reminders, and the category is paused when it passes.'}
              {' '}If the same file was uploaded for other categories at the same time, they are approved together with this one.
            </p>

            {isInsuranceLinkedDoc(insuranceVerifyModal) && !resolveInsuranceType(insuranceVerifyModal) && (
              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-1">Insurance Type</label>
                <select
                  value={insuranceVerifyType}
                  onChange={(e) => setInsuranceVerifyType(e.target.value as InsuranceType)}
                  className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white focus:border-brand-500 focus:outline-none"
                >
                  {INSURANCE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This document's type doesn't say which coverage it proves — pick the one that matches the certificate.
                </p>
              </div>
            )}

            {asksInsurer(insuranceVerifyModal) && (
              <>
                <label className="block text-xs text-gray-500 mb-1">
                  {isInsuranceLinkedDoc(insuranceVerifyModal) ? 'Provider' : 'Insurance company'}
                </label>
                <input
                  type="text" autoFocus
                  value={insuranceVerifyProvider}
                  onChange={(e) => setInsuranceVerifyProvider(e.target.value)}
                  placeholder="e.g. Intact Insurance"
                  className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4"
                />
              </>
            )}

            {/* FIX: previously admin was the ONLY source of the
                expiry date — typed in by reading it off the document
                image, with nothing to check that reading against. A
                misread or a typo silently set the wrong waiver
                window. expertDeclaredExpiresAt is what the expert
                themselves entered at upload time — shown here as a
                reference so admin has a second data point to compare
                against what they see on the actual certificate,
                before typing the date that actually governs the
                waiver below. This does not replace admin's own entry
                as the source of truth — it's a cross-check, not an
                autofill admin can blindly accept. */}
            {insuranceVerifyModal.expertDeclaredExpiresAt && (
              <div className="mb-3 px-3 py-2 bg-surface-tertiary/60 border border-border-light rounded-lg">
                <p className="text-xs text-gray-400">
                  The tasker entered{' '}
                  <span className="text-white font-medium">
                    {new Date(insuranceVerifyModal.expertDeclaredExpiresAt).toLocaleDateString()}
                  </span>
                  {' '}as the expiry date — it is filled in below. Check it against the certificate and change it if the certificate shows a different date.
                </p>
                {!!insuranceVerifyExpiry && !insuranceVerifyNoExpiry
                  && insuranceVerifyExpiry !== declaredExpiryInput(insuranceVerifyModal) && (
                  <p className="text-xs text-amber-400 mt-1">
                    You changed the tasker's date. The date you enter here is the one that is saved.
                  </p>
                )}
              </div>
            )}
            {!insuranceVerifyModal.expertDeclaredExpiresAt && (
              <p className="text-xs text-gray-500 mb-3">
                The tasker did not enter an expiry date (they said it has none, or uploaded it before this was asked). Read it from the certificate.
              </p>
            )}

            <label className="block text-xs text-gray-500 mb-1.5">Expiry Date</label>
            <input
              type="date"
              value={insuranceVerifyExpiry}
              onChange={(e) => setInsuranceVerifyExpiry(e.target.value)}
              disabled={insuranceVerifyNoExpiry}
              // FIX: a bare `type="date"` input under these Tailwind
              // classes rendered with the browser's native calendar
              // icon and value text visually overlapping the
              // surrounding label/placeholder in some browsers — the
              // input had no explicit height or line-height, so the
              // native date UI (which reserves its own internal
              // padding for the icon) collided with this component's
              // padding instead of sitting inside it cleanly.
              // color-scheme: dark tells the browser to render its
              // native date-picker UI (the calendar icon, the
              // dropdown calendar itself) in a dark palette matching
              // the rest of this page, instead of the default light
              // widget that clashed with the dark input background it
              // sits inside. h-11 + leading-normal give the native
              // control a fixed, adequate box to render in rather
              // than inheriting a height that was too tight for its
              // own icon.
              className="w-full h-11 leading-normal bg-surface-tertiary border border-border-light rounded-xl px-4 text-white focus:border-brand-500 focus:outline-none mb-2 disabled:opacity-40"
              style={{ colorScheme: 'dark' }}
            />
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={insuranceVerifyNoExpiry}
                onChange={(e) => { setInsuranceVerifyNoExpiry(e.target.checked); setInsuranceVerifyError('') }}
                className="accent-brand-500"
              />
              This document genuinely has no expiry
            </label>

            {!!insuranceVerifyError && (
              <p className="text-xs text-red-400 mb-4">{insuranceVerifyError}</p>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={() => setInsuranceVerifyModal(null)}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={submitInsuranceVerify} disabled={actingDocId === insuranceVerifyModal.id}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-xl hover:bg-green-500 disabled:opacity-50 transition-colors">
                {actingDocId === insuranceVerifyModal.id ? 'Saving...' : (isInsuranceLinkedDoc(insuranceVerifyModal) ? 'Verify & Record Policy' : 'Confirm & Approve')}
              </button>
            </div>
          </div>
        </div>, document.body)
      )}

      {showRejectExpert && (
        createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => { setShowRejectExpert(false); setRejectExpertReason('') }}>
          <div className="bg-surface-card border border-border-light rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-2">Reject Expert</h3>
            <p className="text-sm text-gray-400 mb-4">
              The expert will be notified with this reason and returned to onboarding to fix it. Be specific enough that they know exactly what to address.
            </p>
            <textarea value={rejectExpertReason} onChange={(e) => setRejectExpertReason(e.target.value)}
              placeholder="e.g. Government ID photo is blurry and address does not match the document on file" rows={3} autoFocus
              className="w-full bg-surface-tertiary border border-border-light rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-brand-500 focus:outline-none mb-4 resize-none" />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowRejectExpert(false); setRejectExpertReason('') }}
                className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">Cancel</button>
              <button onClick={handleRejectExpert} disabled={!rejectExpertReason.trim() || rejectingExpert}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-500 disabled:opacity-50 transition-colors">
                {rejectingExpert ? 'Rejecting...' : 'Reject & Notify Expert'}
              </button>
            </div>
          </div>
        </div>, document.body)
      )}

      {previewDoc && (
        createPortal(
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => closeDocumentPreview()}>
          <div className="bg-surface-card border border-border-light rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border-light shrink-0">
              <p className="text-sm font-medium text-white truncate pr-4">{previewDoc.fileName}</p>
              <div className="flex items-center gap-3 shrink-0">
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand-400 hover:text-brand-300"
                  >
                    Open in new tab ↗
                  </a>
                )}
                <button onClick={() => closeDocumentPreview()} className="text-gray-500 hover:text-white text-lg">✕</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden min-h-0">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full p-8">
                  <p className="text-sm text-gray-400">Opening document…</p>
                </div>
              ) : previewError ? (
                <div className="flex flex-col items-center justify-center h-full p-8 gap-3 text-center">
                  <p className="text-sm text-red-400 max-w-sm">{previewError}</p>
                  {!previewMfaRequired && (
                    <button onClick={() => openDocumentPreview(previewDoc)}
                      className="px-4 py-2 text-sm bg-surface-tertiary text-gray-300 rounded-xl hover:bg-border-light transition-colors">
                      Try again
                    </button>
                  )}
                </div>
              ) : previewUrl && /\.(pdf)$/i.test(previewDoc.fileName || '') ? (
                // Google Docs Viewer fetches the PDF server-side — no CORS
                // issue since the browser only talks to Google, not directly
                // to Firebase Storage. The signed URL is passed as a query
                // param; Google fetches it and renders inline. The URL
                // itself is short-lived (see storage.js's PRIVATE_VIEW_TTL_MS
                // on the backend), so this iframe is only ever pointed at a
                // link that was just freshly issued for this open.
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                  className="w-full h-full min-h-[75vh]"
                  title="PDF preview"
                />
              ) : previewUrl ? (
                // Images: cross-origin <img> tags work fine without CORS
                // headers — only <iframe> and fetch() are blocked.
                <div className="flex items-center justify-center h-full p-4 bg-black/20">
                  <img
                    src={previewUrl}
                    alt={previewDoc.fileName || 'Document'}
                    className="max-w-full max-h-[75vh] object-contain rounded-lg"
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>, document.body)
      )}
    </div>
  )
}

// ── Setup after approval ─────────────────────────────────────
// Dated events (approval, Stripe account created, payout verifying / verified /
// restricted / disconnected, GST/HST declared, setup complete) plus where the
// tasker stands right now. Events are recorded from the day this was released on;
// earlier approvals show only the current state.
const SETUP_EVENT_LABELS: Record<string, { label: string; tone: string }> = {
  approved:               { label: 'Approved by an admin',          tone: 'text-green-400' },
  stripe_account_created: { label: 'Stripe account created',        tone: 'text-gray-300' },
  payout_incomplete:      { label: 'Stripe needs more information', tone: 'text-yellow-400' },
  payout_verifying:       { label: 'Stripe is verifying the payout', tone: 'text-yellow-400' },
  payout_verified:        { label: 'Payout verified by Stripe',     tone: 'text-green-400' },
  payout_restricted:      { label: 'Payout restricted by Stripe',   tone: 'text-red-400' },
  payout_disconnected:    { label: 'Disconnected from Stripe',      tone: 'text-red-400' },
  gst_hst_declared:       { label: 'GST/HST declared',              tone: 'text-green-400' },
  setup_complete:         { label: 'Setup complete — app opened for the tasker', tone: 'text-green-400' },
}

const humanDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`
  if (minutes < 60 * 48) return `${Math.round(minutes / 60)} h`
  return `${Math.round(minutes / (60 * 24))} days`
}

const SetupTimelineCard: React.FC<{ expertId: string }> = ({ expertId }) => {
  const [data, setData]   = useState<any>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    setData(null)
    setError('')
    getExpertSetupTimeline(expertId)
      .then((res: any) => { if (alive) setData(res.data.data) })
      .catch((err: any) => {
        console.error('Failed to fetch setup timeline:', err?.response?.status, err?.message)
        if (alive) setError(err?.response?.data?.message || 'Could not load the setup timeline.')
      })
    return () => { alive = false }
  }, [expertId])

  if (error) return <SectionCard title="Setup after approval"><p className="text-sm text-red-400">{error}</p></SectionCard>
  if (!data) return <SectionCard title="Setup after approval"><p className="text-sm text-gray-500">Loading...</p></SectionCard>

  const cur = data.current || {}
  const events: any[] = data.events || []
  const stripeInfo = STRIPE_STATUS_INFO[cur.stripeBankStatus] || { label: String(cur.stripeBankStatus || '—').replace(/_/g, ' '), className: 'text-gray-300' }

  return (
    <SectionCard title="Setup after approval">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {cur.setupComplete ? (
          <span className="px-2 py-1 text-xs rounded-full bg-green-600/20 text-green-400">Setup complete</span>
        ) : cur.stalled ? (
          <span className="px-2 py-1 text-xs rounded-full bg-red-600/20 text-red-400">⚠ Stalled — {cur.daysSinceApproval} days since approval</span>
        ) : (
          <span className="px-2 py-1 text-xs rounded-full bg-yellow-600/20 text-yellow-400">
            Finishing setup{cur.daysSinceApproval != null ? ` — day ${cur.daysSinceApproval}` : ''}
          </span>
        )}
      </div>

      <div className="flex justify-between py-1.5 border-b border-border-light">
        <p className="text-xs text-gray-500">Payout (Stripe)</p>
        <p className={`text-sm text-right max-w-xs ${stripeInfo.className}`}>{stripeInfo.label}</p>
      </div>
      {(cur.stripeRequirementsDue || []).length > 0 && (
        <DetailRow label="Stripe still needs" value={cur.stripeRequirementsDue.join(', ')} />
      )}
      <DetailRow label="GST/HST" value={cur.taxDeclaredAt
        ? `${cur.taxRegistered ? 'Registered' : 'Not registered'} — declared ${new Date(cur.taxDeclaredAt).toLocaleDateString()}`
        : 'Not declared yet'} />

      <p className="text-xs text-gray-500 uppercase mt-4 mb-2">History</p>
      {events.length === 0 ? (
        <p className="text-xs text-gray-600">
          Nothing recorded yet. Events are recorded from the day this feature was released, so taskers approved earlier show only their current state above.
        </p>
      ) : (
        <ol className="space-y-2">
          {events.map((ev) => {
            const meta = SETUP_EVENT_LABELS[ev.type] || { label: String(ev.type).replace(/_/g, ' '), tone: 'text-gray-300' }
            const d = ev.detail || {}
            const extra = [
              Array.isArray(d.requirementsDue) && d.requirementsDue.length ? `Stripe needs: ${d.requirementsDue.join(', ')}` : '',
              ev.type === 'gst_hst_declared' ? (d.registered ? 'registered' : 'not registered') + (d.changed ? ' (changed)' : '') : '',
              ev.type === 'setup_complete' && d.minutesSinceApproval != null ? `${humanDuration(d.minutesSinceApproval)} after approval` : '',
              ev.type === 'approved' && d.firstApproval === false ? 're-approved' : '',
              ev.type === 'stripe_account_created' && d.reconnect ? 'reconnected after a disconnect' : '',
            ].filter(Boolean).join(' · ')
            return (
              <li key={ev.id} className="text-sm border-l-2 border-border-light pl-3">
                <div className={meta.tone}>{meta.label}</div>
                <div className="text-xs text-gray-500">
                  {new Date(ev.createdAt).toLocaleString()}{extra ? ` · ${extra}` : ''}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </SectionCard>
  )
}

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-surface-card border border-border-light rounded-2xl p-5">
    <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">{title}</h2>
    <div className="space-y-1">{children}</div>
  </div>
)

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between py-1.5 border-b border-border-light last:border-0">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm text-gray-300 text-right max-w-xs">{value}</p>
  </div>
)

const TaskDetailPanel: React.FC<{ task: any; onClose: () => void }> = ({ task, onClose }) => {
  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
  // Use actual stored fee fields rather than hardcoded percentages.
  // taskerPayout is the net amount after commission + all tasker-side
  // fees; it is locked at hire and may be revised downward at job start
  // if an insurance waiver lapses.
  const taskerPayout = task.taskerPayout ?? null

  // commissionAmount is not a stored column (see schema.prisma's own
  // comment on Task) — it's the one number that has to be derived from
  // the ones that ARE stored, using the exact same formula
  // computeHireBreakdown uses to arrive at taskerPayout in the first
  // place. Without this, the admin sees "Base Price $100 → Payout $85"
  // with no way to see the $15 gap was actually commission + tasker-side
  // fees combined — this is the number that makes that gap legible.
  const commissionAmount =
    task.finalPrice != null && taskerPayout != null
      ? round2(
          task.finalPrice
          + (task.taxOnJob ?? 0)
          - taskerPayout
          - (task.insuranceFeeTasker ?? 0)
          - (task.processingFeeTasker ?? 0)
          - (task.serviceFeeTasker ?? 0)
          - (task.taxFeeTasker ?? 0)
        )
      : null

  return (
    <div className="w-96 bg-surface-card border border-border-light rounded-2xl p-5 h-fit sticky top-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Job Details</h3>
        <button onClick={onClose} className="text-gray-500 hover:text-white text-lg">✕</button>
      </div>
      <p className="text-base font-semibold text-white mb-1">{task.title}</p>
      <p className="text-xs text-gray-500 mb-4">{task.category?.name} · {task.urgencyLevel?.name}</p>

      <p className="text-xs text-gray-500 uppercase mb-2">Location</p>
      <DetailRow label="Area" value={task.area || '—'} />
      <DetailRow label="Address" value={task.address || '—'} />

      {(task as any).category?.isTwoLocation && (
        <>
          <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Pickup Details</p>
          <DetailRow label="Pickup Location" value={(task as any).restaurantName || '—'} />
          <DetailRow label="Pickup Address"  value={(task as any).restaurantAddress || '—'} />
          {(task as any).deliveryFee != null && (
            <DetailRow label="Delivery Fee" value={`$${(task as any).deliveryFee.toFixed(2)}`} />
          )}
          {(task as any).category?.requiresPickupCode && (
            <DetailRow label="Pickup Code" value={(task as any).pickupCode || '—'} />
          )}
        </>
      )}

      <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Payment — what the customer paid</p>
      <DetailRow label="Base Price" value={task.finalPrice != null ? `$${task.finalPrice.toFixed(2)}` : '—'} />
      {task.insuranceFee != null && task.insuranceFee > 0 && (
        <DetailRow label="Insurance (Customer)" value={`$${task.insuranceFee.toFixed(2)}`} />
      )}
      {task.serviceFee != null && task.serviceFee > 0 && (
        <DetailRow label="Service Fee (Customer)" value={`$${task.serviceFee.toFixed(2)}`} />
      )}
      {task.processingFee != null && task.processingFee > 0 && (
        <DetailRow label="Processing Fee (Customer)" value={`$${task.processingFee.toFixed(2)}`} />
      )}
      {task.taxAmount != null && task.taxAmount > 0 && (
        <DetailRow label="Tax" value={`$${task.taxAmount.toFixed(2)}`} />
      )}
      <DetailRow label={task.paymentStatus === 'authorized' ? 'Total On Hold' : 'Total Charged'} value={task.totalCharged != null ? `$${task.totalCharged.toFixed(2)}` : '—'} />
      {task.paymentStatus === 'authorized' && (
        <p className="text-xs text-blue-300 pt-1">The customer’s card is only held — it is charged when the job is confirmed.</p>
      )}

      {/* Everything above is what the CUSTOMER paid. Everything below
          is what came OUT of the tasker's own payout — these two sides
          don't mirror each other 1:1 (insurance/service default to
          100% customer, 0% tasker, per-category overridable), so both
          need to be shown rather than assuming one implies the other. */}
      <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Payment — what the tasker was deducted</p>
      <DetailRow label="Commission" value={commissionAmount != null ? `$${commissionAmount.toFixed(2)}` : '—'} />
      {task.insuranceFeeTasker != null && task.insuranceFeeTasker > 0 && (
        <DetailRow label="Insurance (Tasker)" value={`$${task.insuranceFeeTasker.toFixed(2)}`} />
      )}
      {task.serviceFeeTasker != null && task.serviceFeeTasker > 0 && (
        <DetailRow label="Service Fee (Tasker)" value={`$${task.serviceFeeTasker.toFixed(2)}`} />
      )}
      {task.processingFeeTasker != null && task.processingFeeTasker > 0 && (
        <DetailRow label="Processing Fee (Tasker)" value={`$${task.processingFeeTasker.toFixed(2)}`} />
      )}
      {(task.taxOnJob ?? 0) > 0 && <DetailRow label="Tax on job price (customer paid, tasker remits)" value={`+$${task.taxOnJob.toFixed(2)}`} />}
      {(task.taxFeeTasker ?? 0) > 0 && <DetailRow label="Tax on fees (Tasker)" value={`-$${task.taxFeeTasker.toFixed(2)}`} />}
      {/* How tax was worked out for this booking (audit). */}
      {task.taxJurisdictionLabel && (
        <DetailRow label="Tax used" value={`${task.taxJurisdictionLabel}${task.taxProvinceApplied ? ` · ${task.taxProvinceApplied}` : ''}${task.taxExpertRegisteredApplied != null ? ` · tasker ${task.taxExpertRegisteredApplied ? 'registered' : 'not registered'}` : ''}${task.taxPricingModeApplied === 'TAX_INCLUDED' ? ` · tax included in price ($${Number(task.taxIncludedInJob || 0).toFixed(2)})` : task.taxPricingModeApplied === 'TAX_ADDED' ? ' · tax added to price' : ''}`} />
      )}
      {/* The FINAL net (after Stripe's exact fee) replaces the estimate once recorded. */}
      <DetailRow
        label={task.stripeFeeRecordedAt && task.taskerNetFinal != null ? 'Tasker Payout (Net, final)' : 'Tasker Payout (Net, estimate)'}
        value={taskerPayout != null ? `$${(task.stripeFeeRecordedAt && task.taskerNetFinal != null ? task.taskerNetFinal : taskerPayout).toFixed(2)}` : '—'} />

      <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Timing</p>
      <DetailRow label="Posted" value={formatDate(task.createdAt)} />
      <DetailRow label="Hired" value={formatDate(task.hiredAt)} />
    </div>
  )
}

export default ExpertDetailPage